import { useState, useEffect, useRef } from "react";
import { GlassCard, Badge, FormInput, FormSelect, Btn, PasswordChecklist, isPasswordStrong } from "../components/UI";
import { useSchools }  from "../hooks/useSchools";
import { useSessions } from "../hooks/useSessions";
import { supabase }    from "../lib/supabase";

const WEEKS = [1,2,3,4,5,6];

// ── Idle auto-logout (security) ─────────────────────────────────────────────
const IDLE_LIMIT_MS    = 5 * 60 * 1000; // 5 minutes
const IDLE_WARNING_MS  = 30 * 1000;     // show warning 30s before logout

function WeekDots({ attendance = [] }) {
  return (
    <div style={{ display:"flex", gap:5 }}>
      {WEEKS.map((w) => {
        const present = attendance.some((a) => a.week_number === w && a.attended);
        return (
          <div key={w} style={{ flex:1, borderRadius:8, padding:"5px 0", textAlign:"center", background: present ? "rgba(34,197,94,0.15)" : "rgba(255,255,255,0.03)", border:`1.5px solid ${present ? "rgba(34,197,94,0.4)" : "rgba(255,255,255,0.07)"}` }}>
            <div style={{ fontSize:"0.68rem", fontWeight:800, color: present ? "#22C55E" : "rgba(255,255,255,0.25)" }}>W{w}</div>
            <div style={{ fontSize:"0.8rem", marginTop:1 }}>{present ? "✓" : "–"}</div>
          </div>
        );
      })}
    </div>
  );
}

export default function StaffPanel({ staffUser, setPage, setStaffUser }) {
  const isAdmin = staffUser.role === "admin";
  const { schools, setSchools } = useSchools();
  const { sessions: allSessions, loading: sessionsLoading } = useSessions({ adminMode: isAdmin });
  const [activeTab, setActiveTab] = useState(isAdmin ? "parents" : "roster");

  // ── Idle auto-logout (security) ───────────────
  const [showIdleWarning, setShowIdleWarning] = useState(false);
  const [idleCountdown,   setIdleCountdown]   = useState(IDLE_WARNING_MS / 1000);
  const idleTimerRef      = useRef(null);
  const warningTimerRef   = useRef(null);
  const countdownIntervalRef = useRef(null);

  async function handleIdleLogout() {
    await supabase.auth.signOut();
    setStaffUser?.(null);
    setPage("home");
  }

  function resetIdleTimer() {
    clearTimeout(idleTimerRef.current);
    clearTimeout(warningTimerRef.current);
    clearInterval(countdownIntervalRef.current);
    setShowIdleWarning(false);
    setIdleCountdown(IDLE_WARNING_MS / 1000);

    warningTimerRef.current = setTimeout(() => {
      setShowIdleWarning(true);
      setIdleCountdown(IDLE_WARNING_MS / 1000);
      countdownIntervalRef.current = setInterval(() => {
        setIdleCountdown((s) => Math.max(0, s - 1));
      }, 1000);
    }, IDLE_LIMIT_MS - IDLE_WARNING_MS);

    idleTimerRef.current = setTimeout(() => {
      clearInterval(countdownIntervalRef.current);
      handleIdleLogout();
    }, IDLE_LIMIT_MS);
  }

  useEffect(() => {
    const activityEvents = ["mousemove", "mousedown", "keydown", "scroll", "touchstart"];
    resetIdleTimer();
    activityEvents.forEach((ev) => window.addEventListener(ev, resetIdleTimer));
    return () => {
      activityEvents.forEach((ev) => window.removeEventListener(ev, resetIdleTimer));
      clearTimeout(idleTimerRef.current);
      clearTimeout(warningTimerRef.current);
      clearInterval(countdownIntervalRef.current);
    };
  }, []);

  // ── Admin: parents ───────────────────────────
  const [parents, setParents]               = useState([]);
  const [parentsLoading, setParentsLoading] = useState(true);
  const [expandedParent, setExpandedParent] = useState(null);
  const [parentSearch, setParentSearch]     = useState("");
  const [parentSchoolFilter, setParentSchoolFilter] = useState("");
  const [parentSessionFilter, setParentSessionFilter] = useState("");
  const [showContactList, setShowContactList] = useState(false);
  const [emailCopied, setEmailCopied] = useState(false);
  const [namesCopied, setNamesCopied] = useState(false);
  const [expandedStudent, setExpandedStudent] = useState(null); // full student object with enrollments

  useEffect(() => {
    if (!isAdmin) return;
    supabase
      .from("profiles")
      .select("*, students(*, enrollments(*, sessions(*), attendance(*)))")
      .order("created_at", { ascending: false })
      .then(({ data }) => { if (data) setParents(data); setParentsLoading(false); });
  }, [isAdmin]);

  // ── Admin: sessions ──────────────────────────
  const [adminSessions, setAdminSessions]   = useState([]);
  const [sessionTabSchool, setSessionTabSchool] = useState("");
  const [editingSession, setEditingSession] = useState(null);
  const [showAddSession, setShowAddSession] = useState(false);
  const [viewingSessionSignups, setViewingSessionSignups] = useState(null);
  const [sessionListEmailCopied, setSessionListEmailCopied] = useState(false);
  const [sessionListNamesCopied, setSessionListNamesCopied] = useState(false);
  const [sessionSaving, setSessionSaving]   = useState(false);
  const [sessionError, setSessionError]     = useState("");
  const [newSession, setNewSession] = useState({ school_id:"", title:"", days:"", time:"", dates:"", spots:"", price:"" });
  const [newSchoolMode, setNewSchoolMode] = useState(false);
  const [newSchool, setNewSchool]         = useState({ name:"", location:"" });

  useEffect(() => {
    if (allSessions.length === 0) return;
    setAdminSessions(allSessions.map((s) => ({ ...s, schoolName: schools.find((sc) => sc.id === s.school_id)?.name ?? "" })));
  }, [allSessions, schools]);

  async function handleAddSession() {
    if (!newSession.title) return;
    if (newSchoolMode && (!newSchool.name || !newSchool.location)) { setSessionError("School name and location are required."); return; }
    if (!newSchoolMode && !newSession.school_id) { setSessionError("Please select a school."); return; }
    setSessionSaving(true); setSessionError("");

    let schoolId = Number(newSession.school_id);

    if (newSchoolMode) {
      const { data: createdSchool, error: schoolErr } = await supabase
        .from("schools")
        .insert({ name: newSchool.name, location: newSchool.location })
        .select()
        .single();
      if (schoolErr) { setSessionError(schoolErr.message); setSessionSaving(false); return; }
      schoolId = createdSchool.id;
      setSchools((prev) => [...prev, createdSchool]);
    }

    const payload = { school_id: schoolId, title: newSession.title, days: newSession.days, time: newSession.time, dates: newSession.dates, spots: Number(newSession.spots)||0, price: Number(newSession.price)||0 };
    const { data: insertedSession, error } = await supabase.from("sessions").insert(payload).select().single();
    if (error) { setSessionError(error.message); setSessionSaving(false); return; }
    const schoolName = newSchoolMode ? newSchool.name : (schools.find((s) => s.id === schoolId)?.name ?? "");
    setAdminSessions((prev) => [...prev, { ...insertedSession, schoolName }]);
    setNewSession({ school_id:"", title:"", days:"", time:"", dates:"", spots:"", price:"" });
    setNewSchool({ name:"", location:"" });
    setNewSchoolMode(false);
    setShowAddSession(false); setSessionSaving(false);
  }

  async function handleEditSession() {
    if (!editingSession) return;
    setSessionSaving(true); setSessionError("");
    const { _idx, schoolName, ...payload } = editingSession;
    const { error } = await supabase.from("sessions").update({ title: payload.title, days: payload.days, time: payload.time, dates: payload.dates, spots: Number(payload.spots), price: Number(payload.price) }).eq("id", payload.id);
    if (error) { setSessionError(error.message); setSessionSaving(false); return; }
    setAdminSessions((prev) => prev.map((s, i) => i === _idx ? { ...editingSession, schoolName } : s));
    setEditingSession(null); setSessionSaving(false);
  }

  async function toggleSessionVisibility(sessionId, currentVisible) {
    const newVisible = !currentVisible;
    const { error } = await supabase.from("sessions").update({ visible: newVisible }).eq("id", sessionId);
    if (error) return;
    setAdminSessions((prev) => prev.map((s) => s.id === sessionId ? { ...s, visible: newVisible } : s));
  }

  async function toggleSessionSiblingDiscount(sessionId, currentEnabled) {
    const newEnabled = !currentEnabled;
    const { error } = await supabase.from("sessions").update({ sibling_discount_eligible: newEnabled }).eq("id", sessionId);
    if (error) return;
    setAdminSessions((prev) => prev.map((s) => s.id === sessionId ? { ...s, sibling_discount_eligible: newEnabled } : s));
  }

  // ── Admin: staff ─────────────────────────────
  const [staffMembers, setStaffMembers]         = useState([]);
  const [staffLoading, setStaffLoading]         = useState(true);
  const [showAddStaff, setShowAddStaff]         = useState(false);
  const [staffAdded, setStaffAdded]             = useState(false);
  const [staffCreateError, setStaffCreateError] = useState("");
  const [staffCreating, setStaffCreating]       = useState(false);
  const [newStaff, setNewStaff] = useState({ name:"", email:"", password:"", role:"teacher", school_id:"" });
  const [adminRoleConfirmed, setAdminRoleConfirmed] = useState(false);
  const [editingStaff, setEditingStaff]             = useState(null); // { id, name, role }
  const [staffEditSaving, setStaffEditSaving]       = useState(false);
  const [staffEditError, setStaffEditError]         = useState("");
  const [editAdminConfirmed, setEditAdminConfirmed] = useState(false);

  useEffect(() => {
    if (!isAdmin) return;
    supabase.from("staff_profiles").select("*").order("created_at", { ascending: false })
      .then(({ data }) => { if (data) setStaffMembers(data); setStaffLoading(false); });
  }, [isAdmin]);

  async function handleEditStaff() {
    if (!editingStaff) return;
    if (!editingStaff.name.trim()) { setStaffEditError("Name is required."); return; }
    if (editingStaff.role === "admin" && !editAdminConfirmed) { setStaffEditError("Please confirm the admin role change."); return; }
    setStaffEditSaving(true); setStaffEditError("");
    const { error } = await supabase
      .from("staff_profiles")
      .update({ name: editingStaff.name.trim(), role: editingStaff.role })
      .eq("id", editingStaff.id);
    if (error) { setStaffEditError(error.message); setStaffEditSaving(false); return; }
    setStaffMembers((prev) => prev.map((m) => m.id === editingStaff.id ? { ...m, name: editingStaff.name.trim(), role: editingStaff.role } : m));
    setEditingStaff(null); setStaffEditSaving(false); setEditAdminConfirmed(false);
  }

  const [togglingActiveId, setTogglingActiveId] = useState(null);

  async function handleToggleActive(staffId, currentlyActive) {
    setTogglingActiveId(staffId);
    const { error } = await supabase
      .from("staff_profiles")
      .update({ active: !currentlyActive })
      .eq("id", staffId);
    setTogglingActiveId(null);
    if (error) { console.error("Error updating staff status:", error.message); return; }
    setStaffMembers((prev) => prev.map((m) => m.id === staffId ? { ...m, active: !currentlyActive } : m));
  }

  // ── Password reset (shared for staff + parents) ───
  const [pwReset, setPwReset]           = useState(null); // { id, name }
  const [pwValue, setPwValue]           = useState("");
  const [pwSaving, setPwSaving]         = useState(false);
  const [pwError, setPwError]           = useState("");
  const [pwSuccess, setPwSuccess]       = useState(false);
  const [pwVisible, setPwVisible]       = useState(false);
  const [pwConfirmed, setPwConfirmed]   = useState(false);

  async function handleResetPassword() {
    if (!pwReset || !isPasswordStrong(pwValue)) { setPwError("Password must be at least 8 characters and include an uppercase letter, a lowercase letter, a number, and a symbol."); return; }
    setPwSaving(true); setPwError("");
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) { setPwError("Your session has expired. Please log out and log back in."); setPwSaving(false); return; }
    const { data, error } = await supabase.functions.invoke("update-user-password", {
      body: { userId: pwReset.id, newPassword: pwValue },
    });
    if (error || !data?.success) {
      let msg = data?.error ?? error?.message ?? "Failed to update password.";
      try { const body = await error?.context?.json?.(); if (body?.error) msg = body.error; } catch (_) {}
      setPwError(msg); setPwSaving(false); return;
    }
    setPwSuccess(true); setPwSaving(false);
  }

  function openPwReset(id, name) {
    setPwReset({ id, name });
    setPwValue(""); setPwError(""); setPwSuccess(false); setPwVisible(false); setPwConfirmed(false);
  }

  async function handleCreateStaff() {
    setStaffCreateError("");
    if (!newStaff.name || !newStaff.email || !newStaff.password) return;
    setStaffCreating(true);
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) { setStaffCreateError("Your session has expired. Please log out and log back in."); setStaffCreating(false); return; }
    const { data, error } = await supabase.functions.invoke("create-staff-user", {
      body: { name: newStaff.name, email: newStaff.email, password: newStaff.password, role: newStaff.role, school_id: null },
    });
    if (error || !data?.success) {
      let msg = data?.error ?? error?.message ?? "Failed to create account.";
      try { const body = await error?.context?.json?.(); if (body?.error) msg = body.error; } catch (_) {}
      setStaffCreateError(msg); setStaffCreating(false); return;
    }
    const { data: updated } = await supabase.from("staff_profiles").select("*").order("created_at", { ascending: false });
    if (updated) setStaffMembers(updated);
    setStaffAdded(true); setStaffCreating(false);
  }

  // ── Admin: attendance report ─────────────────
  const [adminAttendance, setAdminAttendance]       = useState([]);
  const [adminAttLoading, setAdminAttLoading]       = useState(false);
  const [adminAttLoaded, setAdminAttLoaded]         = useState(false);
  const [attSchool, setAttSchool]                   = useState("");
  const [attSession, setAttSession]                 = useState("");

  useEffect(() => {
    if (!isAdmin || activeTab !== "attendance" || adminAttLoaded) return;
    setAdminAttLoading(true);
    supabase
      .from("enrollments")
      .select("*, sessions(*), students(*), attendance(*), student_notes(*, reporter:staff_profiles!updated_by(name))")
      .then(({ data }) => {
        setAdminAttendance(data ?? []);
        setAdminAttLoading(false);
        setAdminAttLoaded(true);
      });
  }, [isAdmin, activeTab, adminAttLoaded]);

  const attSchoolSessions = attSchool
    ? allSessions.filter((s) => s.school_id == attSchool)
    : [];

  const filteredAdminAtt = adminAttendance.filter((e) => {
    if (attSchool && e.sessions?.school_id != attSchool) return false;
    if (attSession && e.session_id != attSession) return false;
    return true;
  });

  // Per-week stats for the selected filter
  const weekStats = WEEKS.map((w) => {
    const total = filteredAdminAtt.length;
    const present = filteredAdminAtt.filter((e) => (e.attendance ?? []).some((a) => a.week_number === w && a.attended)).length;
    return { week: w, present, total };
  });

  // ── Teacher: roster ──────────────────────────
  const [rosterEnrollments, setRosterEnrollments] = useState([]);
  const [rosterLoading, setRosterLoading]         = useState(true);
  const [rosterSchool, setRosterSchool]           = useState("");
  const [rosterSession, setRosterSession]         = useState("");
  const [attendanceMap, setAttendanceMap]         = useState({});
  const [weekUpdating, setWeekUpdating]           = useState({});
  const [notesMap, setNotesMap]                   = useState({}); // { enrollmentId: { week: { note, updated_at, updaterName } } }
  const [expandedNoteId, setExpandedNoteId]       = useState(null);
  const [noteWeek, setNoteWeek]                   = useState(1);
  const [noteText, setNoteText]                   = useState("");
  const [noteSaving, setNoteSaving]               = useState(false);

  useEffect(() => {
    if (isAdmin || sessionsLoading) return;
    if (allSessions.length === 0) { setRosterLoading(false); return; }
    supabase
      .from("enrollments")
      .select("*, sessions(*), students(*, profiles(*)), attendance(*), student_notes(*, reporter:staff_profiles!updated_by(name))")
      .then(({ data }) => {
        if (data) {
          setRosterEnrollments(data);
          const attMap = {};
          const nMap   = {};
          data.forEach((e) => {
            (e.attendance ?? []).forEach((row) => {
              if (!attMap[e.id]) attMap[e.id] = {};
              attMap[e.id][row.week_number] = row.attended;
            });
            (e.student_notes ?? []).forEach((row) => {
              if (!nMap[e.id]) nMap[e.id] = {};
              nMap[e.id][row.week_number] = { note: row.note, updated_at: row.updated_at, updaterName: row.reporter?.name ?? "Unknown" };
            });
          });
          setAttendanceMap(attMap);
          setNotesMap(nMap);
        }
        setRosterLoading(false);
      });
  }, [allSessions, sessionsLoading, isAdmin]);

  async function toggleWeek(enrollmentId, week, current) {
    const key = `${enrollmentId}_${week}`;
    setWeekUpdating((prev) => ({ ...prev, [key]: true }));
    const newVal = !current;
    await supabase.from("attendance").upsert(
      { enrollment_id: enrollmentId, week_number: week, attended: newVal, updated_by: staffUser.id, updated_at: new Date().toISOString() },
      { onConflict: "enrollment_id,week_number" }
    );
    setAttendanceMap((prev) => ({
      ...prev,
      [enrollmentId]: { ...(prev[enrollmentId] ?? {}), [week]: newVal },
    }));
    setWeekUpdating((prev) => ({ ...prev, [key]: false }));
    setHistoryLoaded(false);
  }

  async function saveNote(enrollmentId) {
    if (!noteText.trim()) return;
    setNoteSaving(true);
    const now = new Date().toISOString();
    const { error } = await supabase.from("student_notes").upsert(
      { enrollment_id: enrollmentId, week_number: noteWeek, note: noteText.trim(), updated_by: staffUser.id, updated_at: now },
      { onConflict: "enrollment_id,week_number" }
    );
    if (!error) {
      setNotesMap((prev) => ({
        ...prev,
        [enrollmentId]: { ...(prev[enrollmentId] ?? {}), [noteWeek]: { note: noteText.trim(), updated_at: now, updaterName: staffUser.name } },
      }));
      setHistoryLoaded(false);
    }
    setNoteSaving(false);
  }

  // ── Admin: promo codes ───────────────────────
  const [promoCodes, setPromoCodes]     = useState([]);
  const [promoLoading, setPromoLoading] = useState(false);
  const [showAddPromo, setShowAddPromo] = useState(false);
  const [newPromo, setNewPromo]         = useState({ code:"", description:"", discount_amount:"", max_uses:"", unlimited:true });
  const [promoSaving, setPromoSaving]   = useState(false);
  const [promoError, setPromoError]     = useState("");

  // Always re-fetch when the tab opens so current_uses stays up to date
  useEffect(() => {
    if (!isAdmin || activeTab !== "promos") return;
    setPromoLoading(true);
    supabase.from("promo_codes").select("*").order("created_at", { ascending: false })
      .then(({ data }) => { setPromoCodes(data ?? []); setPromoLoading(false); });
  }, [isAdmin, activeTab]);

  async function handleAddPromo() {
    if (!newPromo.code.trim() || !newPromo.discount_amount) { setPromoError("Code and discount amount are required."); return; }
    setPromoSaving(true); setPromoError("");
    const payload = {
      code: newPromo.code.toUpperCase().trim(),
      description: newPromo.description.trim() || null,
      discount_amount: Number(newPromo.discount_amount),
      max_uses: newPromo.unlimited ? null : (Number(newPromo.max_uses) || null),
      active: true,
    };
    const { data, error } = await supabase.from("promo_codes").insert(payload).select().single();
    if (error) { setPromoError(error.message); setPromoSaving(false); return; }
    setPromoCodes((prev) => [data, ...prev]);
    setNewPromo({ code:"", description:"", discount_amount:"", max_uses:"", unlimited:true });
    setShowAddPromo(false); setPromoSaving(false);
  }

  async function togglePromoActive(id, current) {
    await supabase.from("promo_codes").update({ active: !current }).eq("id", id);
    setPromoCodes((prev) => prev.map((p) => p.id === id ? { ...p, active: !current } : p));
  }

  async function deletePromo(id) {
    await supabase.from("promo_codes").delete().eq("id", id);
    setPromoCodes((prev) => prev.filter((p) => p.id !== id));
  }

  // ── Admin: history ───────────────────────────
  const [historyData, setHistoryData]   = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyLoaded, setHistoryLoaded]   = useState(false);
  const [histSchool, setHistSchool]         = useState("");
  const [histSession, setHistSession]       = useState("");

  useEffect(() => {
    if (!isAdmin || activeTab !== "history" || historyLoaded) return;
    setHistoryLoading(true);
    Promise.all([
      supabase
        .from("attendance")
        .select("*, enrollments(*, sessions(*), students(*)), reporter:staff_profiles!updated_by(name, role)")
        .not("updated_by", "is", null)
        .order("updated_at", { ascending: false })
        .limit(300),
      supabase
        .from("student_notes")
        .select("*, enrollments(*, sessions(*), students(*)), reporter:staff_profiles!updated_by(name, role)")
        .order("updated_at", { ascending: false })
        .limit(300),
    ]).then(([attRes, notesRes]) => {
      const att   = (attRes.data   ?? []).map((r) => ({ ...r, _type: "attendance" }));
      const notes = (notesRes.data ?? []).map((r) => ({ ...r, _type: "note" }));
      const merged = [...att, ...notes].sort((a, b) => new Date(b.updated_at) - new Date(a.updated_at));
      setHistoryData(merged);
      setHistoryLoading(false);
      setHistoryLoaded(true);
    });
  }, [isAdmin, activeTab, historyLoaded]);

  const tabs = isAdmin
    ? [["parents","👨‍👩‍👧 Parents"],["sessions","📅 Sessions"],["staff","👥 Staff"],["attendance","📊 Attendance"],["history","📜 History"],["promos","🎟️ Promos"]]
    : [["roster","📋 My Roster"]];

  return (
    <div className="admin-panel" style={{ minHeight:"100vh", padding:"6rem 1.5rem 3rem" }}>

      {/* Idle auto-logout warning */}
      {showIdleWarning && (
        <div style={{ position:"fixed", inset:0, zIndex:500, background:"rgba(0,0,0,0.78)", backdropFilter:"blur(4px)", display:"flex", alignItems:"center", justifyContent:"center", padding:"1rem", animation:"fadeIn 0.2s ease" }}>
          <div style={{ background:"rgba(10,13,48,0.98)", border:"1.5px solid rgba(245,158,11,0.35)", borderRadius:24, padding:"2.4rem 2.2rem", maxWidth:400, width:"100%", textAlign:"center", boxShadow:"0 32px 80px rgba(0,0,0,0.6), 0 0 0 1px rgba(245,158,11,0.15)" }}>
            <div style={{ fontSize:"2.2rem", marginBottom:"0.5rem" }}>⏱️</div>
            <div style={{ fontFamily:"'Fredoka One',cursive", fontSize:"1.3rem", color:"#fff", marginBottom:"0.5rem" }}>Still there?</div>
            <p style={{ color:"rgba(255,255,255,0.55)", fontSize:"0.87rem", fontWeight:600, lineHeight:1.65, marginBottom:"1.3rem" }}>
              For security, you'll be signed out of the Staff Panel due to inactivity in
            </p>
            <div style={{ fontFamily:"'Fredoka One',cursive", fontSize:"2.6rem", color:"#F59E0B", marginBottom:"1.5rem", lineHeight:1 }}>
              {idleCountdown}s
            </div>
            <div style={{ display:"flex", gap:"0.7rem" }}>
              <Btn onClick={handleIdleLogout} variant="ghost" style={{ flex:1 }}>Sign Out Now</Btn>
              <Btn onClick={resetIdleTimer} variant="primary" style={{ flex:1 }}>Stay Signed In</Btn>
            </div>
          </div>
          <style>{`
            @keyframes fadeIn { from { opacity:0 } to { opacity:1 } }
          `}</style>
        </div>
      )}

      <div style={{ maxWidth:1100, margin:"0 auto" }}>

        {/* Header */}
        <div style={{ background:"linear-gradient(135deg,rgba(155,92,246,0.15),rgba(59,130,246,0.12))", border:"1.5px solid rgba(155,92,246,0.25)", borderRadius:22, padding:"1.6rem 2rem", marginBottom:"1.8rem", display:"flex", alignItems:"center", justifyContent:"space-between", flexWrap:"wrap", gap:"1rem" }}>
          <div>
            <div style={{ fontFamily:"'Fredoka One',cursive", fontSize:"1.7rem" }}>⚡ {isAdmin ? "Admin Panel" : "Teacher Panel"}</div>
            <div style={{ color:"rgba(255,255,255,0.45)", fontSize:"0.86rem", fontWeight:600, marginTop:3, display:"flex", gap:8, alignItems:"center", flexWrap:"wrap" }}>
              Logged in as <strong style={{ color:"#9B5CF6" }}>{staffUser.name}</strong>
              <Badge color={isAdmin ? "#F59E0B" : "#3B82F6"} small>{staffUser.role}</Badge>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="admin-tabs" style={{ display:"flex", gap:5, marginBottom:"1.8rem", background:"rgba(255,255,255,0.05)", borderRadius:14, padding:5, flexWrap:"nowrap" }}>
          {tabs.map(([id, label]) => (
            <button key={id} onClick={() => setActiveTab(id)} style={{ padding:"8px 20px", borderRadius:10, border:"none", cursor:"pointer", background:activeTab===id?"#9B5CF6":"transparent", color:activeTab===id?"#fff":"rgba(255,255,255,0.5)", fontFamily:"inherit", fontWeight:800, fontSize:"0.87rem", transition:"all 0.2s", whiteSpace:"nowrap" }}>{label}</button>
          ))}
        </div>

        {/* ════════════════════════════════════
            PARENTS TAB
        ════════════════════════════════════ */}
        {activeTab === "parents" && isAdmin && (() => {
          const schoolSessionStats = parentSchoolFilter
            ? adminSessions
                .filter((s) => String(s.school_id) === String(parentSchoolFilter))
                .map((s) => {
                  const count = parents.reduce((acc, p) =>
                    acc + (p.students ?? []).filter((st) =>
                      (st.enrollments ?? []).some((e) => e.session_id === s.id)
                    ).length
                  , 0);
                  return { ...s, signupCount: count };
                })
            : [];

          return (
          <div>
            <div className="parent-header-row" style={{ display:"flex", alignItems:"center", justifyContent:"space-between", flexWrap:"wrap", gap:"0.75rem", marginBottom: schoolSessionStats.length ? "0.75rem" : "1.2rem" }}>
              <div style={{ fontFamily:"'Fredoka One',cursive", fontSize:"1.2rem" }}>
                All Enrolled Families ({parentsLoading ? "…" : parents.length})
              </div>
              <div className="parent-filters-row" style={{ display:"flex", gap:"0.6rem", flexWrap:"wrap", alignItems:"center" }}>
                {/* School filter */}
                <select
                  value={parentSchoolFilter}
                  onChange={(e) => { setParentSchoolFilter(e.target.value); setParentSessionFilter(""); }}
                  style={{ background:"rgba(10,13,50,0.95)", border:`1.5px solid ${parentSchoolFilter ? "rgba(155,92,246,0.5)" : "rgba(255,255,255,0.15)"}`, borderRadius:10, padding:"8px 14px", color: parentSchoolFilter ? "#fff" : "rgba(255,255,255,0.4)", fontFamily:"inherit", fontWeight:700, fontSize:"0.84rem", cursor:"pointer", outline:"none" }}>
                  <option value="">All Schools</option>
                  {schools.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
                {parentSchoolFilter && (
                  <button onClick={() => { setParentSchoolFilter(""); setParentSessionFilter(""); }} style={{ background:"rgba(155,92,246,0.12)", border:"1.5px solid rgba(155,92,246,0.3)", color:"#9B5CF6", borderRadius:8, padding:"6px 10px", fontSize:"0.8rem", cursor:"pointer", fontFamily:"inherit", fontWeight:700 }}>✕ Clear</button>
                )}
                {/* Name/email search */}
                <div style={{ flex:"1 1 200px", display:"flex", alignItems:"center", gap:0, background:"rgba(255,255,255,0.06)", border:"1.5px solid rgba(255,255,255,0.14)", borderRadius:10, overflow:"hidden" }}>
                  <span style={{ paddingLeft:12, fontSize:"0.9rem", opacity:0.4, pointerEvents:"none", flexShrink:0, lineHeight:1 }}>🔍</span>
                  <input
                    value={parentSearch}
                    onChange={(e) => setParentSearch(e.target.value)}
                    placeholder="Search by name, email, phone…"
                    style={{ flex:1, background:"transparent", border:"none", padding:"8px 10px", color:"#fff", fontFamily:"inherit", fontWeight:600, fontSize:"0.84rem", outline:"none", minWidth:0 }}
                  />
                  {parentSearch && (
                    <button onClick={() => setParentSearch("")} style={{ paddingRight:10, background:"none", border:"none", color:"rgba(255,255,255,0.4)", cursor:"pointer", fontSize:"1rem", lineHeight:1, flexShrink:0 }}>×</button>
                  )}
                </div>
              </div>
            </div>

            {/* Session pills — click to filter parents to that session only */}
            {schoolSessionStats.length > 0 && (
              <div style={{ display:"flex", gap:"0.6rem", flexWrap:"wrap", marginBottom:"1.2rem", padding:"0.75rem 1rem", background:"rgba(155,92,246,0.07)", border:"1.5px solid rgba(155,92,246,0.2)", borderRadius:14, alignItems:"center" }}>
                <span style={{ fontSize:"0.75rem", fontWeight:700, color:"rgba(255,255,255,0.4)", textTransform:"uppercase", letterSpacing:"0.05em", marginRight:4 }}>Sessions:</span>
                {schoolSessionStats.map((s) => {
                  const active = parentSessionFilter === String(s.id);
                  return (
                    <button key={s.id}
                      onClick={() => { setParentSessionFilter(active ? "" : String(s.id)); setEmailCopied(false); setNamesCopied(false); }}
                      style={{ display:"flex", alignItems:"center", gap:6, background: active ? "rgba(155,92,246,0.2)" : "rgba(255,255,255,0.05)", border:`1.5px solid ${active ? "rgba(155,92,246,0.6)" : "rgba(255,255,255,0.12)"}`, borderRadius:10, padding:"4px 10px", cursor:"pointer", fontFamily:"inherit", transition:"border-color 0.18s, background 0.18s" }}
                      onMouseEnter={(e) => { if (!active) { e.currentTarget.style.borderColor="rgba(155,92,246,0.4)"; e.currentTarget.style.background="rgba(155,92,246,0.08)"; } }}
                      onMouseLeave={(e) => { if (!active) { e.currentTarget.style.borderColor="rgba(255,255,255,0.12)"; e.currentTarget.style.background="rgba(255,255,255,0.05)"; } }}>
                      <span style={{ fontSize:"0.8rem", fontWeight:700, color: active ? "#C084FC" : "#fff" }}>{s.title.replace("Mars Academy — ","")}</span>
                      <span style={{ fontSize:"0.75rem", fontWeight:800, color: s.signupCount > 0 ? "#22C55E" : "rgba(255,255,255,0.3)", background: s.signupCount > 0 ? "rgba(34,197,94,0.12)" : "rgba(255,255,255,0.05)", border:`1px solid ${s.signupCount > 0 ? "rgba(34,197,94,0.3)" : "rgba(255,255,255,0.1)"}`, borderRadius:6, padding:"1px 7px" }}>{s.signupCount} signed up</span>
                    </button>
                  );
                })}
                {parentSessionFilter && (
                  <button onClick={() => { setShowContactList(true); setEmailCopied(false); setNamesCopied(false); }}
                    style={{ marginLeft:"auto", display:"flex", alignItems:"center", gap:5, background:"rgba(59,130,246,0.15)", border:"1.5px solid rgba(59,130,246,0.4)", color:"#60A5FA", borderRadius:10, padding:"5px 12px", cursor:"pointer", fontFamily:"inherit", fontWeight:700, fontSize:"0.8rem", whiteSpace:"nowrap" }}>
                    📋 Contact List
                  </button>
                )}
              </div>
            )}

            {parentsLoading
              ? <div style={{ textAlign:"center", padding:"3rem", color:"rgba(255,255,255,0.35)", fontWeight:600 }}>Loading families…</div>
              : (() => {
                  const q = parentSearch.toLowerCase().trim();
                  const bySearch = q
                    ? parents.filter((p) =>
                        p.name?.toLowerCase().includes(q) ||
                        p.email?.toLowerCase().includes(q) ||
                        p.phone?.toLowerCase().includes(q) ||
                        (p.students ?? []).some((s) => s.name?.toLowerCase().includes(q))
                      )
                    : parents;
                  const bySchool = parentSchoolFilter
                    ? bySearch.filter((p) =>
                        (p.students ?? []).some((st) =>
                          (st.enrollments ?? []).some((e) => String(e.sessions?.school_id) === String(parentSchoolFilter))
                        )
                      )
                    : bySearch;
                  const visible = parentSessionFilter
                    ? bySchool.filter((p) =>
                        (p.students ?? []).some((st) =>
                          (st.enrollments ?? []).some((e) => String(e.session_id) === parentSessionFilter)
                        )
                      )
                    : bySchool;
                  if (visible.length === 0) return (
                    <div style={{ textAlign:"center", padding:"3rem", color:"rgba(255,255,255,0.35)", fontWeight:600 }}>
                      {q || parentSchoolFilter ? `No families match your filters.` : "No parent accounts yet."}
                    </div>
                  );
                  return <div style={{ display:"flex", flexDirection:"column", gap:"0.8rem" }}>
                  {visible.map((p) => {
                    const allEnr = (p.students ?? []).flatMap((s) => s.enrollments ?? []);
                    const paidCount = allEnr.filter((e) => e.paid).length;
                    return (
                      <div key={p.id} style={{ background:"rgba(255,255,255,0.04)", border:"1.5px solid rgba(255,255,255,0.1)", borderRadius:16, overflow:"hidden" }}>
                        <div onClick={() => setExpandedParent(expandedParent === p.id ? null : p.id)} style={{ padding:"1rem 1.4rem", cursor:"pointer", display:"flex", flexDirection:"column", gap:"0.55rem" }}>
                          {/* Name row with chevron pinned right */}
                          <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", gap:"0.75rem" }}>
                            <div style={{ display:"flex", gap:12, alignItems:"center", flex:1, minWidth:0 }}>
                              <div style={{ width:38, height:38, borderRadius:"50%", background:"rgba(155,92,246,0.2)", border:"1.5px solid rgba(155,92,246,0.4)", display:"flex", alignItems:"center", justifyContent:"center", fontSize:"1.1rem", flexShrink:0 }}>👤</div>
                              <div style={{ minWidth:0 }}>
                                <div style={{ fontWeight:800, fontSize:"0.95rem", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{p.name}</div>
                                <div style={{ color:"rgba(255,255,255,0.42)", fontSize:"0.78rem", fontWeight:600, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{p.email} · {p.phone}</div>
                              </div>
                            </div>
                            <span style={{ color:"rgba(255,255,255,0.4)", fontSize:"1rem", flexShrink:0 }}>{expandedParent === p.id ? "▲" : "▼"}</span>
                          </div>
                          {/* Badges always on their own row */}
                          <div style={{ display:"flex", gap:6, flexWrap:"wrap" }}>
                            <Badge color="#FFD93D" small>{(p.students ?? []).length} student{(p.students ?? []).length !== 1 ? "s" : ""}</Badge>
                            <Badge color={paidCount > 0 ? "#22C55E" : "#F59E0B"} small>{paidCount}/{allEnr.length} paid</Badge>
                            <Badge color={p.consent_agreed_at ? "#22C55E" : "#FF6B6B"} small>
                              {p.consent_agreed_at ? `✓ Consent ${p.consent_version ?? "V1.0"}` : "⚠ No Consent"}
                            </Badge>
                          </div>
                        </div>
                        {expandedParent === p.id && (
                          <div style={{ borderTop:"1px solid rgba(255,255,255,0.08)", padding:"1.2rem 1.4rem" }}>
                            {/* Actions row */}
                            <div style={{ display:"flex", justifyContent:"flex-end", marginBottom:"0.75rem" }}>
                              <button
                                onClick={() => openPwReset(p.id, p.name)}
                                style={{ background:"rgba(245,158,11,0.12)", border:"1px solid rgba(245,158,11,0.3)", color:"#F59E0B", borderRadius:8, padding:"5px 14px", fontSize:"0.78rem", cursor:"pointer", fontFamily:"inherit", fontWeight:700 }}>
                                🔑 Reset Password
                              </button>
                            </div>
                            {/* Consent status row */}
                            <div style={{ marginBottom:"1rem", padding:"10px 14px", borderRadius:10, background: p.consent_agreed_at ? "rgba(34,197,94,0.07)" : "rgba(255,107,107,0.07)", border:`1.5px solid ${p.consent_agreed_at ? "rgba(34,197,94,0.25)" : "rgba(255,107,107,0.25)"}`, display:"flex", alignItems:"center", gap:10, flexWrap:"wrap" }}>
                              <span style={{ fontSize:"1rem" }}>{p.consent_agreed_at ? "✅" : "⚠️"}</span>
                              <div style={{ flex:1, minWidth:160 }}>
                                <div style={{ fontSize:"0.76rem", fontWeight:800, color: p.consent_agreed_at ? "#22C55E" : "#FF6B6B" }}>
                                  {p.consent_agreed_at ? `Enrollment Agreement Accepted — ${p.consent_version ?? "V1.0"}` : "Enrollment Agreement Not Signed"}
                                </div>
                                {p.consent_agreed_at && (
                                  <div style={{ fontSize:"0.71rem", fontWeight:600, color:"rgba(255,255,255,0.4)", marginTop:2 }}>
                                    Agreed on {new Date(p.consent_agreed_at).toLocaleString("en-US", { dateStyle:"medium", timeStyle:"short" })}
                                  </div>
                                )}
                              </div>
                            </div>
                            <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(200px,1fr))", gap:"0.9rem" }}>
                            {(p.students ?? []).map((s) => {
                              const enrCount = (s.enrollments ?? []).length;
                              const paidCount = (s.enrollments ?? []).filter((e) => e.paid).length;
                              return (
                                <div key={s.id} onClick={() => setExpandedStudent({ ...s, parentName: p.name })}
                                  style={{ background:"rgba(255,255,255,0.04)", borderRadius:14, padding:"1rem 1.1rem", cursor:"pointer", border:"1.5px solid rgba(255,255,255,0.08)", transition:"all 0.18s" }}
                                  onMouseEnter={(e) => { e.currentTarget.style.border="1.5px solid rgba(255,217,61,0.35)"; e.currentTarget.style.background="rgba(255,217,61,0.05)"; }}
                                  onMouseLeave={(e) => { e.currentTarget.style.border="1.5px solid rgba(255,255,255,0.08)"; e.currentTarget.style.background="rgba(255,255,255,0.04)"; }}
                                >
                                  <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:"0.6rem" }}>
                                    <div style={{ fontFamily:"'Fredoka One',cursive", fontSize:"0.95rem", color:"#FFD93D" }}>🧑‍🚀 {s.name}</div>
                                    <span style={{ fontSize:"0.7rem", color:"rgba(255,255,255,0.3)", fontWeight:700 }}>View →</span>
                                  </div>
                                  {[["Grade", s.grade],["Age", s.age]].map(([k, v]) => (
                                    <div key={k} style={{ display:"flex", justifyContent:"space-between", fontSize:"0.78rem", marginBottom:3 }}>
                                      <span style={{ color:"rgba(255,255,255,0.38)", fontWeight:600 }}>{k}</span>
                                      <span style={{ fontWeight:700 }}>{v || "—"}</span>
                                    </div>
                                  ))}
                                  <div style={{ marginTop:"0.6rem", display:"flex", gap:5, flexWrap:"wrap" }}>
                                    <Badge color="#9B5CF6" small>{enrCount} class{enrCount !== 1 ? "es" : ""}</Badge>
                                    <Badge color={paidCount === enrCount && enrCount > 0 ? "#22C55E" : "#F59E0B"} small>{paidCount}/{enrCount} paid</Badge>
                                  </div>
                                </div>
                              );
                            })}
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>;
                })()
            }
          </div>
          );
        })()}

        {/* ════════════════════════════════════
            SESSIONS TAB
        ════════════════════════════════════ */}
        {activeTab === "sessions" && isAdmin && (() => {
          const filteredSessions = sessionTabSchool
            ? adminSessions.filter((s) => String(s.school_id) === sessionTabSchool)
            : adminSessions;
          const signupCountBySession = {};
          parents.forEach((p) => {
            (p.students ?? []).forEach((st) => {
              (st.enrollments ?? []).forEach((e) => {
                signupCountBySession[e.session_id] = (signupCountBySession[e.session_id] ?? 0) + 1;
              });
            });
          });
          return (
          <div>
            <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:"1.2rem", flexWrap:"wrap", gap:"0.75rem" }}>
              <div style={{ fontFamily:"'Fredoka One',cursive", fontSize:"1.2rem" }}>
                {sessionTabSchool
                  ? <>{schools.find((s) => String(s.id) === sessionTabSchool)?.name ?? "School"} <span style={{ color:"rgba(255,255,255,0.35)", fontWeight:400 }}>({filteredSessions.length})</span></>
                  : <>All Sessions ({adminSessions.length})</>
                }
              </div>
              <div style={{ display:"flex", gap:"0.6rem", flexWrap:"wrap", alignItems:"center" }}>
                <select
                  value={sessionTabSchool}
                  onChange={(e) => setSessionTabSchool(e.target.value)}
                  style={{ background:"rgba(10,13,50,0.95)", border:`1.5px solid ${sessionTabSchool ? "rgba(155,92,246,0.5)" : "rgba(255,255,255,0.15)"}`, borderRadius:10, padding:"8px 14px", color: sessionTabSchool ? "#fff" : "rgba(255,255,255,0.4)", fontFamily:"inherit", fontWeight:700, fontSize:"0.84rem", cursor:"pointer", outline:"none" }}>
                  <option value="">All Schools</option>
                  {schools.map((s) => <option key={s.id} value={String(s.id)}>{s.name}</option>)}
                </select>
                {sessionTabSchool && (
                  <button onClick={() => setSessionTabSchool("")} style={{ background:"rgba(155,92,246,0.12)", border:"1.5px solid rgba(155,92,246,0.3)", color:"#9B5CF6", borderRadius:8, padding:"6px 10px", fontSize:"0.8rem", cursor:"pointer", fontFamily:"inherit", fontWeight:700 }}>✕ All Schools</button>
                )}
                <Btn onClick={() => { setSessionError(""); setShowAddSession(true); }} variant="purple" style={{ padding:"8px 20px", fontSize:"0.85rem" }}>+ New Session</Btn>
              </div>
            </div>
            {sessionsLoading
              ? <div style={{ textAlign:"center", padding:"3rem", color:"rgba(255,255,255,0.35)", fontWeight:600 }}>Loading sessions…</div>
              : filteredSessions.length === 0
              ? <div style={{ textAlign:"center", padding:"4rem 2rem", background:"rgba(255,255,255,0.03)", border:"1.5px dashed rgba(155,92,246,0.25)", borderRadius:20 }}>
                  <div style={{ fontSize:"2.5rem", marginBottom:"0.8rem" }}>📅</div>
                  <div style={{ fontFamily:"'Fredoka One',cursive", fontSize:"1.2rem", color:"rgba(255,255,255,0.5)" }}>No sessions for this school yet</div>
                </div>
              : <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(280px,1fr))", gap:"1.1rem" }}>
                {filteredSessions.map((s) => {
                  const i = adminSessions.indexOf(s);
                  const visible = s.visible !== false;
                  const siblingDiscountOn = s.sibling_discount_eligible !== false;
                  const signupCount = parentsLoading ? null : (signupCountBySession[s.id] ?? 0);
                  return (
                  <div key={s.id} style={{ background: visible ? "rgba(255,255,255,0.04)" : "rgba(255,255,255,0.02)", border:`1.5px solid ${visible ? "rgba(255,255,255,0.1)" : "rgba(255,107,107,0.2)"}`, borderRadius:16, padding:"1.1rem 1.2rem", opacity: visible ? 1 : 0.75 }}>
                    <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:6, gap:6 }}>
                      <div style={{ fontWeight:800, fontSize:"0.9rem", flex:1 }}>{s.title}</div>
                      <button onClick={() => { setSessionError(""); setEditingSession({ ...s, _idx:i }); }} style={{ background:"rgba(155,92,246,0.15)", border:"1px solid rgba(155,92,246,0.3)", color:"#9B5CF6", borderRadius:8, padding:"3px 10px", fontSize:"0.72rem", cursor:"pointer", fontFamily:"inherit", fontWeight:700, flexShrink:0 }}>Edit</button>
                    </div>
                    <div style={{ color:"rgba(255,255,255,0.42)", fontSize:"0.78rem", fontWeight:600, marginBottom:6 }}>{s.schoolName}</div>
                    <div style={{ color:"rgba(255,255,255,0.55)", fontSize:"0.8rem", fontWeight:600, marginBottom:6 }}>{s.days} · {s.time} · {s.dates}</div>
                    <div style={{ display:"flex", gap:6, alignItems:"center", flexWrap:"wrap" }}>
                      <Badge color={s.price === 0 ? "#22C55E" : "#FFD93D"} small>{s.price === 0 ? "FREE" : `$${s.price}`}</Badge>
                      <Badge color={s.spots > 0 && (s.enrolled_count ?? 0) >= s.spots ? "#FF6B6B" : "#3B82F6"} small>
                        {s.spots > 0 ? `${s.enrolled_count ?? 0}/${s.spots} spots` : `${s.enrolled_count ?? 0} enrolled`}
                      </Badge>
                      {signupCount > 0
                        ? <button onClick={() => { setSessionListEmailCopied(false); setSessionListNamesCopied(false); setViewingSessionSignups(s); }} style={{ background:"rgba(34,197,94,0.12)", border:"1.5px solid rgba(34,197,94,0.35)", color:"#22C55E", borderRadius:8, padding:"3px 10px", fontSize:"0.72rem", cursor:"pointer", fontFamily:"inherit", fontWeight:700 }}>
                            👥 {signupCount} signed up
                          </button>
                        : <Badge color="rgba(255,255,255,0.3)" small>{signupCount === null ? "…" : "0 signed up"}</Badge>
                      }
                      <button
                        onClick={() => toggleSessionSiblingDiscount(s.id, siblingDiscountOn)}
                        title={siblingDiscountOn ? "Sibling discount applies to this class" : "Sibling discount does not apply to this class"}
                        style={{ display:"flex", alignItems:"center", gap:5, background: siblingDiscountOn ? "rgba(155,92,246,0.12)" : "rgba(255,255,255,0.06)", border:`1.5px solid ${siblingDiscountOn ? "rgba(155,92,246,0.35)" : "rgba(255,255,255,0.15)"}`, color: siblingDiscountOn ? "#C084FC" : "rgba(255,255,255,0.4)", borderRadius:8, padding:"4px 10px", cursor:"pointer", fontFamily:"inherit", fontWeight:700, fontSize:"0.72rem", whiteSpace:"nowrap" }}>
                        {siblingDiscountOn ? "🎉 Sibling -10%" : "🚫 No Sibling Discount"}
                      </button>
                      <div style={{ marginLeft:"auto" }}>
                        <button
                          onClick={() => toggleSessionVisibility(s.id, visible)}
                          style={{ display:"flex", alignItems:"center", gap:5, background: visible ? "rgba(34,197,94,0.12)" : "rgba(255,107,107,0.12)", border:`1.5px solid ${visible ? "rgba(34,197,94,0.35)" : "rgba(255,107,107,0.35)"}`, color: visible ? "#22C55E" : "#FF6B6B", borderRadius:8, padding:"4px 10px", cursor:"pointer", fontFamily:"inherit", fontWeight:700, fontSize:"0.72rem", whiteSpace:"nowrap" }}>
                          {visible ? "👁 Visible" : "🚫 Hidden"}
                        </button>
                      </div>
                    </div>
                    {!visible && (
                      <div style={{ marginTop:8, fontSize:"0.72rem", fontWeight:700, color:"rgba(255,107,107,0.7)", letterSpacing:"0.03em" }}>
                        Hidden from parents — sign-ups closed
                      </div>
                    )}
                  </div>
                  );
                })}
              </div>
            }
          </div>
          );
        })()}

        {/* ════════════════════════════════════
            STAFF TAB
        ════════════════════════════════════ */}
        {activeTab === "staff" && isAdmin && (
          <div>
            <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:"1.2rem", flexWrap:"wrap", gap:"0.75rem" }}>
              <div style={{ fontFamily:"'Fredoka One',cursive", fontSize:"1.2rem" }}>Teaching Staff ({staffLoading ? "…" : staffMembers.length})</div>
              <Btn onClick={() => { setStaffCreateError(""); setAdminRoleConfirmed(false); setShowAddStaff(true); }} variant="success" style={{ padding:"8px 20px", fontSize:"0.85rem" }}>+ Add Staff Member</Btn>
            </div>
            {staffLoading
              ? <div style={{ textAlign:"center", padding:"3rem", color:"rgba(255,255,255,0.35)", fontWeight:600 }}>Loading staff…</div>
              : staffMembers.length === 0
                ? <div style={{ textAlign:"center", padding:"3rem", color:"rgba(255,255,255,0.35)", fontWeight:600 }}>No staff accounts yet.</div>
                : <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(280px,1fr))", gap:"1.1rem" }}>
                  {staffMembers.map((t) => {
                    const isSelf = t.id === staffUser.id;
                    const isActive = t.active !== false;
                    return (
                    <GlassCard key={t.id} hover={false} style={{ opacity: isActive ? 1 : 0.55 }}>
                      <div style={{ display:"flex", gap:12, alignItems:"center", marginBottom:"0.75rem" }}>
                        <div style={{ width:46, height:46, borderRadius:"50%", background: t.role==="admin"?"rgba(245,158,11,0.2)":"rgba(59,130,246,0.2)", border:`1.5px solid ${t.role==="admin"?"rgba(245,158,11,0.4)":"rgba(59,130,246,0.4)"}`, display:"flex", alignItems:"center", justifyContent:"center", fontSize:"1.3rem", flexShrink:0 }}>{t.role==="admin"?"🛡️":"👩‍🏫"}</div>
                        <div style={{ minWidth:0 }}>
                          <div style={{ fontWeight:800, fontSize:"0.95rem", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{t.name}</div>
                          <div style={{ color:"rgba(255,255,255,0.42)", fontSize:"0.78rem", fontWeight:600, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{t.email}</div>
                        </div>
                      </div>
                      <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", gap:6, flexWrap:"wrap" }}>
                        <div style={{ display:"flex", gap:5, flexWrap:"wrap" }}>
                          <Badge color={t.role==="admin"?"#F59E0B":"#3B82F6"} small>{t.role}</Badge>
                          {!isActive && <Badge color="#FF6B6B" small>Inactive</Badge>}
                        </div>
                        <div style={{ display:"flex", gap:5, flexWrap:"wrap" }}>
                          <button
                            onClick={() => { setStaffEditError(""); setEditAdminConfirmed(false); setEditingStaff({ id:t.id, name:t.name, role:t.role, email:t.email }); }}
                            style={{ background:"rgba(155,92,246,0.15)", border:"1px solid rgba(155,92,246,0.3)", color:"#9B5CF6", borderRadius:8, padding:"4px 12px", fontSize:"0.72rem", cursor:"pointer", fontFamily:"inherit", fontWeight:700 }}>
                            Edit
                          </button>
                          <button
                            onClick={() => openPwReset(t.id, t.name)}
                            style={{ background:"rgba(245,158,11,0.12)", border:"1px solid rgba(245,158,11,0.3)", color:"#F59E0B", borderRadius:8, padding:"4px 12px", fontSize:"0.72rem", cursor:"pointer", fontFamily:"inherit", fontWeight:700 }}>
                            🔑 Password
                          </button>
                          <button
                            onClick={() => handleToggleActive(t.id, isActive)}
                            disabled={isSelf || togglingActiveId === t.id}
                            title={isSelf ? "You can't deactivate your own account" : ""}
                            style={{
                              background: isActive ? "rgba(255,107,107,0.12)" : "rgba(34,197,94,0.12)",
                              border: `1px solid ${isActive ? "rgba(255,107,107,0.3)" : "rgba(34,197,94,0.3)"}`,
                              color: isActive ? "#FF6B6B" : "#22C55E",
                              borderRadius:8, padding:"4px 12px", fontSize:"0.72rem",
                              cursor: (isSelf || togglingActiveId === t.id) ? "default" : "pointer",
                              fontFamily:"inherit", fontWeight:700,
                              opacity: (isSelf || togglingActiveId === t.id) ? 0.5 : 1,
                            }}>
                            {togglingActiveId === t.id ? "…" : isActive ? "Deactivate" : "Activate"}
                          </button>
                        </div>
                      </div>
                    </GlassCard>
                    );
                  })}
                </div>
            }
          </div>
        )}

        {/* ════════════════════════════════════
            ATTENDANCE TAB (admin)
        ════════════════════════════════════ */}
        {activeTab === "attendance" && isAdmin && (
          <div>
            {/* Filters */}
            <div style={{ display:"flex", gap:"0.85rem", flexWrap:"wrap", marginBottom:"1.6rem", alignItems:"flex-end" }}>
              <div>
                <div style={{ fontSize:"0.75rem", fontWeight:700, color:"rgba(255,255,255,0.4)", textTransform:"uppercase", letterSpacing:"0.05em", marginBottom:5 }}>School</div>
                <select className="admin-filter-select" value={attSchool} onChange={(e) => { setAttSchool(e.target.value); setAttSession(""); }}
                  style={{ background:"rgba(10,13,50,0.95)", border:"1.5px solid rgba(255,255,255,0.15)", borderRadius:10, padding:"8px 14px", color:"#fff", fontFamily:"inherit", fontWeight:700, fontSize:"0.84rem", cursor:"pointer", outline:"none", minWidth:180 }}>
                  <option value="">All Schools</option>
                  {schools.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
              </div>
              <div>
                <div style={{ fontSize:"0.75rem", fontWeight:700, color:"rgba(255,255,255,0.4)", textTransform:"uppercase", letterSpacing:"0.05em", marginBottom:5 }}>Session</div>
                <select className="admin-filter-select" value={attSession} onChange={(e) => setAttSession(e.target.value)}
                  style={{ background:"rgba(10,13,50,0.95)", border:"1.5px solid rgba(255,255,255,0.15)", borderRadius:10, padding:"8px 14px", color:"#fff", fontFamily:"inherit", fontWeight:700, fontSize:"0.84rem", cursor:"pointer", outline:"none", minWidth:200 }}
                  disabled={!attSchool}>
                  <option value="">All Sessions</option>
                  {attSchoolSessions.map((s) => <option key={s.id} value={s.id}>{s.title.replace("Mars Academy — ","")}</option>)}
                </select>
              </div>
            </div>

            {adminAttLoading
              ? <div style={{ textAlign:"center", padding:"3rem", color:"rgba(255,255,255,0.35)", fontWeight:600 }}>Loading attendance…</div>
              : filteredAdminAtt.length === 0
                ? <div style={{ textAlign:"center", padding:"4rem 2rem", background:"rgba(255,255,255,0.03)", border:"1.5px dashed rgba(155,92,246,0.25)", borderRadius:20 }}>
                    <div style={{ fontSize:"2.5rem", marginBottom:"0.8rem" }}>📊</div>
                    <div style={{ fontFamily:"'Fredoka One',cursive", fontSize:"1.2rem", color:"rgba(255,255,255,0.5)" }}>No attendance data yet</div>
                  </div>
                : <>
                  {/* Week summary bar */}
                  <div className="att-week-grid" style={{ display:"grid", gridTemplateColumns:"repeat(6,1fr)", gap:"0.6rem", marginBottom:"1.8rem" }}>
                    {weekStats.map(({ week, present, total }) => {
                      const pct = total > 0 ? Math.round((present / total) * 100) : 0;
                      const color = pct >= 80 ? "#22C55E" : pct >= 50 ? "#F59E0B" : "#FF6B6B";
                      return (
                        <div key={week} style={{ background:"rgba(255,255,255,0.04)", border:`1.5px solid ${color}33`, borderRadius:14, padding:"0.9rem 0.7rem", textAlign:"center" }}>
                          <div style={{ fontFamily:"'Fredoka One',cursive", fontSize:"1rem", color, marginBottom:4 }}>W{week}</div>
                          <div style={{ fontSize:"1.3rem", fontWeight:800, color }}>{present}</div>
                          <div style={{ fontSize:"0.7rem", color:"rgba(255,255,255,0.35)", fontWeight:600 }}>of {total}</div>
                          <div style={{ marginTop:6, height:4, borderRadius:4, background:"rgba(255,255,255,0.08)", overflow:"hidden" }}>
                            <div style={{ height:"100%", width:`${pct}%`, background: color, borderRadius:4, transition:"width 0.4s" }} />
                          </div>
                          <div style={{ fontSize:"0.68rem", color, fontWeight:700, marginTop:3 }}>{pct}%</div>
                        </div>
                      );
                    })}
                  </div>

                  {/* Per-student rows */}
                  <div style={{ display:"flex", flexDirection:"column", gap:"0.65rem" }}>
                    {filteredAdminAtt.map((e) => {
                      const student = e.students;
                      const session = e.sessions;
                      const schoolName = schools.find((sc) => sc.id === session?.school_id)?.name ?? "";
                      const weeksPresent = WEEKS.filter((w) => (e.attendance ?? []).some((a) => a.week_number === w && a.attended)).length;
                      const pct = Math.round((weeksPresent / 6) * 100);
                      const barColor = pct >= 80 ? "#22C55E" : pct >= 50 ? "#F59E0B" : pct > 0 ? "#FF6B6B" : "rgba(255,255,255,0.1)";
                      return (
                        <div key={e.id} style={{ background:"rgba(255,255,255,0.04)", border:"1.5px solid rgba(255,255,255,0.09)", borderRadius:16, padding:"1rem 1.4rem" }}>
                          <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", flexWrap:"wrap", gap:"0.75rem", marginBottom:"0.85rem" }}>
                            <div style={{ display:"flex", alignItems:"center", gap:10 }}>
                              <div style={{ width:36, height:36, borderRadius:10, background:"rgba(255,217,61,0.12)", border:"1.5px solid rgba(255,217,61,0.25)", display:"flex", alignItems:"center", justifyContent:"center", fontSize:"1rem" }}>🧑‍🚀</div>
                              <div>
                                <div style={{ fontWeight:800, fontSize:"0.92rem" }}>{student?.name ?? "—"}</div>
                                <div style={{ color:"rgba(255,255,255,0.38)", fontSize:"0.75rem", fontWeight:600 }}>{schoolName} · {session?.title?.replace("Mars Academy — ","") ?? ""}</div>
                              </div>
                            </div>
                            <div style={{ display:"flex", alignItems:"center", gap:8 }}>
                              <div style={{ width:90, height:6, borderRadius:6, background:"rgba(255,255,255,0.08)", overflow:"hidden" }}>
                                <div style={{ height:"100%", width:`${pct}%`, background: barColor, borderRadius:6, transition:"width 0.4s" }} />
                              </div>
                              <span style={{ fontFamily:"'Fredoka One',cursive", fontSize:"0.9rem", color: barColor }}>{weeksPresent}/6</span>
                            </div>
                          </div>
                          <WeekDots attendance={e.attendance} />
                          {(() => {
                            const allNotes = (e.student_notes ?? []).filter((n) => n.note);
                            if (!allNotes.length) return null;
                            const latest = allNotes.sort((a, b) => new Date(b.updated_at) - new Date(a.updated_at))[0];
                            return (
                              <div style={{ marginTop:"0.75rem", borderTop:"1px solid rgba(155,92,246,0.15)", paddingTop:"0.7rem", display:"flex", gap:8, alignItems:"flex-start" }}>
                                <span style={{ fontSize:"0.7rem", fontWeight:800, color:"#C084FC", flexShrink:0, marginTop:2, background:"rgba(155,92,246,0.15)", border:"1px solid rgba(155,92,246,0.3)", borderRadius:5, padding:"1px 6px" }}>W{latest.week_number}</span>
                                <div style={{ flex:1, minWidth:0 }}>
                                  <div style={{ fontSize:"0.82rem", fontWeight:600, color:"rgba(255,255,255,0.78)", lineHeight:1.45 }}>{latest.note}</div>
                                  <div style={{ fontSize:"0.7rem", color:"rgba(255,255,255,0.3)", fontWeight:600, marginTop:2 }}>
                                    📝 {latest.reporter?.name ?? "Unknown"} · {new Date(latest.updated_at).toLocaleDateString("en-US", { month:"short", day:"numeric" })}
                                  </div>
                                </div>
                              </div>
                            );
                          })()}
                        </div>
                      );
                    })}
                  </div>
                </>
            }
          </div>
        )}

        {/* ════════════════════════════════════
            ROSTER TAB (teacher only)
        ════════════════════════════════════ */}
        {activeTab === "roster" && !isAdmin && (() => {
          const schoolSessions = rosterSchool
            ? allSessions.filter((s) => String(s.school_id) === String(rosterSchool))
            : [];
          const selectedSession = rosterSession
            ? schoolSessions.find((s) => String(s.id) === rosterSession)
            : null;
          return (
          <div>
            <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", flexWrap:"wrap", gap:"0.75rem", marginBottom:"1.4rem" }}>
              <div>
                <div style={{ fontFamily:"'Fredoka One',cursive", fontSize:"1.2rem" }}>My Student Roster</div>
                {rosterSchool && (
                  <p style={{ color:"rgba(255,255,255,0.42)", fontSize:"0.85rem", fontWeight:600, marginTop:3 }}>
                    {schools.find((s) => String(s.id) === String(rosterSchool))?.name}
                    {selectedSession && <span style={{ color:"rgba(155,92,246,0.8)" }}> · {selectedSession.title.replace("Mars Academy — ","")}</span>}
                  </p>
                )}
              </div>
              <div style={{ display:"flex", gap:"0.6rem", flexWrap:"wrap", alignItems:"center" }}>
                <select value={rosterSchool} onChange={(e) => { setRosterSchool(e.target.value); setRosterSession(""); }}
                  style={{ background:"rgba(10,13,50,0.95)", border:`1.5px solid ${rosterSchool ? "rgba(155,92,246,0.5)" : "rgba(255,255,255,0.2)"}`, borderRadius:10, padding:"8px 16px", color: rosterSchool ? "#fff" : "rgba(255,255,255,0.4)", fontFamily:"inherit", fontWeight:700, fontSize:"0.84rem", cursor:"pointer", outline:"none" }}>
                  <option value="" disabled>Select a school…</option>
                  {schools.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
                {schoolSessions.length > 0 && (
                  <select value={rosterSession} onChange={(e) => setRosterSession(e.target.value)}
                    style={{ background:"rgba(10,13,50,0.95)", border:`1.5px solid ${rosterSession ? "rgba(155,92,246,0.5)" : "rgba(255,255,255,0.15)"}`, borderRadius:10, padding:"8px 16px", color: rosterSession ? "#fff" : "rgba(255,255,255,0.4)", fontFamily:"inherit", fontWeight:700, fontSize:"0.84rem", cursor:"pointer", outline:"none" }}>
                    <option value="">All Sessions</option>
                    {schoolSessions.map((s) => <option key={s.id} value={String(s.id)}>{s.title.replace("Mars Academy — ","")}</option>)}
                  </select>
                )}
              </div>
            </div>

            {!rosterSchool
              ? <div style={{ textAlign:"center", padding:"4rem 2rem", background:"rgba(255,255,255,0.03)", border:"1.5px dashed rgba(155,92,246,0.25)", borderRadius:20 }}>
                  <div style={{ fontSize:"2.5rem", marginBottom:"0.8rem" }}>🏫</div>
                  <div style={{ fontFamily:"'Fredoka One',cursive", fontSize:"1.2rem", color:"rgba(255,255,255,0.6)", marginBottom:"0.4rem" }}>Select a school to view its roster</div>
                  <p style={{ color:"rgba(255,255,255,0.3)", fontSize:"0.84rem", fontWeight:600 }}>Use the dropdown above to choose a school.</p>
                </div>
              : rosterLoading
              ? <div style={{ textAlign:"center", padding:"3rem", color:"rgba(255,255,255,0.35)", fontWeight:600 }}>Loading roster…</div>
              : (() => {
                  const bySchool = rosterEnrollments.filter((e) => String(e.sessions?.school_id) === String(rosterSchool) && e.sessions?.visible !== false);
                  const filtered = rosterSession
                    ? bySchool.filter((e) => String(e.session_id) === rosterSession)
                    : bySchool;
                  if (filtered.length === 0) return (
                    <div style={{ textAlign:"center", padding:"3rem", color:"rgba(255,255,255,0.35)", fontWeight:600 }}>
                      {rosterSession ? "No students in this session yet." : "No students enrolled at this school yet."}
                    </div>
                  );
                  return filtered.map((e) => {
                    const student = e.students;
                    const parent  = student?.profiles;
                    const session = e.sessions;
                    if (!student || !parent) return null;
                    const weeks = attendanceMap[e.id] ?? {};
                    const weeksPresent = WEEKS.filter((w) => weeks[w]).length;
                    return (
                      <div key={e.id} style={{ background:"rgba(255,255,255,0.04)", border:"1.5px solid rgba(255,255,255,0.1)", borderRadius:18, padding:"1.2rem 1.4rem", marginBottom:"1rem" }}>
                        {/* Top row: student info + parent contact */}
                        <div className="roster-info-grid" style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:"0.75rem 2rem", marginBottom:"1.1rem" }}>
                          <div>
                            <div style={{ fontFamily:"'Fredoka One',cursive", fontSize:"1rem", color:"#FFD93D", marginBottom:4 }}>🧑‍🚀 {student.name}</div>
                            <div style={{ color:"rgba(255,255,255,0.5)", fontSize:"0.8rem", fontWeight:600 }}>Grade: {student.grade} · Age: {student.age}</div>
                            {student.allergies && student.allergies !== "None" && <div style={{ marginTop:4 }}><Badge color="#FF6B6B" small>⚠️ {student.allergies}</Badge></div>}
                            {student.needs     && student.needs     !== "None" && <div style={{ marginTop:4 }}><Badge color="#F59E0B" small>📋 {student.needs}</Badge></div>}
                          </div>
                          <div style={{ minWidth:0 }}>
                            <div style={{ color:"rgba(255,255,255,0.42)", fontSize:"0.75rem", fontWeight:700, marginBottom:4, textTransform:"uppercase", letterSpacing:"0.05em" }}>Parent</div>
                            <div style={{ fontWeight:800, fontSize:"0.88rem", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{parent.name}</div>
                            <div style={{ color:"rgba(255,255,255,0.55)", fontSize:"0.82rem", fontWeight:600, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{parent.phone}</div>
                            <div style={{ color:"rgba(255,255,255,0.45)", fontSize:"0.78rem", fontWeight:600, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{parent.email}</div>
                            {session && <div style={{ marginTop:5 }}><Badge color="#9B5CF6" small>{session.days} · {session.time}</Badge></div>}
                          </div>
                        </div>

                        {/* Attendance row */}
                        <div style={{ borderTop:"1px solid rgba(255,255,255,0.07)", paddingTop:"0.9rem" }}>
                          <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:"0.6rem" }}>
                            <span style={{ fontSize:"0.75rem", fontWeight:700, color:"rgba(255,255,255,0.4)", textTransform:"uppercase", letterSpacing:"0.05em" }}>Attendance</span>
                            <span style={{ fontSize:"0.78rem", fontWeight:800, color: weeksPresent === 6 ? "#22C55E" : weeksPresent >= 4 ? "#F59E0B" : "rgba(255,255,255,0.4)" }}>{weeksPresent}/6 weeks</span>
                          </div>
                          <div style={{ display:"flex", gap:6 }}>
                            {WEEKS.map((w) => {
                              const present = !!weeks[w];
                              const updKey  = `${e.id}_${w}`;
                              const busy    = !!weekUpdating[updKey];
                              return (
                                <button key={w} onClick={() => !busy && toggleWeek(e.id, w, present)}
                                  style={{ flex:1, borderRadius:10, padding:"8px 4px", border:`1.5px solid ${present ? "#22C55E" : "rgba(255,255,255,0.12)"}`, background: present ? "rgba(34,197,94,0.15)" : "rgba(255,255,255,0.03)", cursor: busy ? "wait" : "pointer", transition:"all 0.18s", display:"flex", flexDirection:"column", alignItems:"center", gap:3 }}>
                                  <span style={{ fontSize:"0.62rem", fontWeight:800, color: present ? "#22C55E" : "rgba(255,255,255,0.35)", letterSpacing:"0.04em" }}>W{w}</span>
                                  <span style={{ fontSize:"0.95rem", lineHeight:1 }}>{busy ? "…" : present ? "✓" : "○"}</span>
                                </button>
                              );
                            })}
                          </div>
                        </div>

                        {/* ── Previous Session Notes (repeating students) ── */}
                        {(() => {
                          const studentId = e.students?.id;
                          const prevNotes = rosterEnrollments
                            .filter((other) => other.students?.id === studentId && other.id !== e.id)
                            .flatMap((other) => (other.student_notes ?? []).filter((n) => n.note?.trim()))
                            .sort((a, b) => new Date(b.updated_at) - new Date(a.updated_at))
                            .slice(0, 2);
                          if (!prevNotes.length) return null;
                          return (
                            <div style={{ marginTop:"0.85rem", padding:"0.85rem 1rem", background:"linear-gradient(135deg,rgba(245,158,11,0.08),rgba(251,191,36,0.04))", border:"1.5px solid rgba(245,158,11,0.28)", borderRadius:14 }}>
                              <div style={{ marginBottom:"0.65rem" }}>
                                <span style={{ fontSize:"0.68rem", fontWeight:800, color:"#F59E0B", textTransform:"uppercase", letterSpacing:"0.06em" }}>🔁 From Previous Sessions</span>
                              </div>
                              <div style={{ display:"flex", flexDirection:"column", gap:"0.6rem" }}>
                                {prevNotes.map((pn, idx) => {
                                  const prevEnrollment = rosterEnrollments.find((other) => other.id === pn.enrollment_id);
                                  const prevSessionName = prevEnrollment?.sessions?.title?.replace("Mars Academy — ","") ?? "Previous Session";
                                  return (
                                    <div key={idx} style={{ display:"flex", gap:8, alignItems:"flex-start", paddingTop: idx > 0 ? "0.6rem" : 0, borderTop: idx > 0 ? "1px solid rgba(245,158,11,0.12)" : "none" }}>
                                      <span style={{ fontSize:"0.68rem", fontWeight:800, color:"#F59E0B", flexShrink:0, marginTop:2, background:"rgba(245,158,11,0.15)", border:"1px solid rgba(245,158,11,0.3)", borderRadius:5, padding:"1px 6px" }}>W{pn.week_number}</span>
                                      <div style={{ flex:1, minWidth:0 }}>
                                        <div style={{ fontSize:"0.83rem", fontWeight:600, color:"rgba(255,255,255,0.85)", lineHeight:1.5 }}>{pn.note}</div>
                                        <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", flexWrap:"wrap", gap:4, marginTop:4 }}>
                                          <div style={{ fontSize:"0.7rem", color:"rgba(255,255,255,0.3)", fontWeight:600 }}>
                                            📝 {pn.reporter?.name ?? "Unknown"} · {new Date(pn.updated_at).toLocaleDateString("en-US",{ month:"short", day:"numeric", year:"numeric" })}
                                          </div>
                                          <span style={{ fontSize:"0.66rem", fontWeight:700, color:"rgba(255,255,255,0.35)", background:"rgba(245,158,11,0.08)", border:"1px solid rgba(245,158,11,0.15)", borderRadius:4, padding:"1px 6px" }}>{prevSessionName}</span>
                                        </div>
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          );
                        })()}

                        {/* ── Curriculum Notes ── */}
                        {(() => {
                          const enrollNotes = notesMap[e.id] ?? {};
                          const noteEntries = Object.entries(enrollNotes).sort((a, b) => new Date(b[1].updated_at) - new Date(a[1].updated_at));
                          const latestEntry = noteEntries[0];
                          const isOpen = expandedNoteId === e.id;
                          return (
                            <div style={{ borderTop:"1px solid rgba(155,92,246,0.15)", paddingTop:"0.9rem", marginTop:"0.85rem" }}>
                              <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:"0.7rem" }}>
                                <span style={{ fontSize:"0.72rem", fontWeight:800, color:"rgba(155,92,246,0.7)", textTransform:"uppercase", letterSpacing:"0.06em" }}>📝 Curriculum Notes</span>
                                <button
                                  onClick={() => {
                                    if (isOpen) { setExpandedNoteId(null); return; }
                                    const defaultWeek = latestEntry ? Number(latestEntry[0]) : 1;
                                    setExpandedNoteId(e.id);
                                    setNoteWeek(defaultWeek);
                                    setNoteText(latestEntry ? latestEntry[1].note : "");
                                  }}
                                  style={{ background: isOpen ? "rgba(255,255,255,0.06)" : "rgba(155,92,246,0.12)", border:`1.5px solid ${isOpen ? "rgba(255,255,255,0.15)" : "rgba(155,92,246,0.35)"}`, color: isOpen ? "rgba(255,255,255,0.45)" : "#C084FC", borderRadius:8, padding:"3px 10px", cursor:"pointer", fontFamily:"inherit", fontWeight:700, fontSize:"0.72rem" }}>
                                  {isOpen ? "✕ Close" : "✏️ Edit"}
                                </button>
                              </div>

                              {/* Latest note preview */}
                              {!isOpen && (
                                latestEntry
                                  ? <div style={{ background:"rgba(155,92,246,0.08)", border:"1.5px solid rgba(155,92,246,0.2)", borderRadius:10, padding:"0.65rem 0.85rem" }}>
                                      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:4 }}>
                                        <span style={{ fontSize:"0.7rem", fontWeight:800, color:"#C084FC", background:"rgba(155,92,246,0.2)", borderRadius:5, padding:"1px 7px" }}>Week {latestEntry[0]}</span>
                                        <span style={{ fontSize:"0.7rem", color:"rgba(255,255,255,0.3)", fontWeight:600 }}>{latestEntry[1].updaterName} · {new Date(latestEntry[1].updated_at).toLocaleDateString("en-US", { month:"short", day:"numeric" })}</span>
                                      </div>
                                      <div style={{ fontSize:"0.83rem", fontWeight:600, color:"rgba(255,255,255,0.82)", lineHeight:1.5 }}>{latestEntry[1].note}</div>
                                    </div>
                                  : <div style={{ fontSize:"0.8rem", fontWeight:600, color:"rgba(255,255,255,0.22)", fontStyle:"italic" }}>No notes yet — click Edit to add one.</div>
                              )}

                              {/* Note editor */}
                              {isOpen && (
                                <div>
                                  {/* Week pills */}
                                  <div style={{ display:"flex", gap:5, marginBottom:"0.75rem" }}>
                                    {WEEKS.map((w) => {
                                      const hasNote  = !!enrollNotes[w];
                                      const isActive = noteWeek === w;
                                      return (
                                        <button key={w}
                                          onClick={() => { setNoteWeek(w); setNoteText(enrollNotes[w]?.note ?? ""); }}
                                          style={{ flex:1, borderRadius:8, padding:"5px 2px", background: isActive ? "rgba(155,92,246,0.25)" : hasNote ? "rgba(155,92,246,0.08)" : "rgba(255,255,255,0.03)", border:`1.5px solid ${isActive ? "rgba(155,92,246,0.7)" : hasNote ? "rgba(155,92,246,0.3)" : "rgba(255,255,255,0.1)"}`, color: isActive ? "#C084FC" : hasNote ? "rgba(192,132,252,0.65)" : "rgba(255,255,255,0.28)", fontFamily:"inherit", fontWeight:800, fontSize:"0.64rem", cursor:"pointer", transition:"all 0.18s", textAlign:"center" }}>
                                          W{w}{hasNote ? " ✓" : ""}
                                        </button>
                                      );
                                    })}
                                  </div>
                                  {/* Textarea */}
                                  <textarea
                                    value={noteText}
                                    onChange={(ev) => setNoteText(ev.target.value)}
                                    placeholder={`Week ${noteWeek} — curriculum level, where they left off, progress…`}
                                    rows={3}
                                    style={{ width:"100%", background:"rgba(255,255,255,0.05)", border:"1.5px solid rgba(155,92,246,0.3)", borderRadius:10, padding:"0.6rem 0.8rem", color:"#fff", fontFamily:"inherit", fontWeight:600, fontSize:"0.83rem", resize:"vertical", outline:"none", boxSizing:"border-box", lineHeight:1.5 }}
                                  />
                                  {enrollNotes[noteWeek] && (
                                    <div style={{ fontSize:"0.7rem", color:"rgba(255,255,255,0.28)", fontWeight:600, marginTop:4 }}>
                                      Last edited by {enrollNotes[noteWeek].updaterName} · {new Date(enrollNotes[noteWeek].updated_at).toLocaleDateString("en-US", { month:"short", day:"numeric", year:"numeric" })}
                                    </div>
                                  )}
                                  <div style={{ display:"flex", justifyContent:"flex-end", marginTop:"0.65rem" }}>
                                    <button
                                      onClick={() => saveNote(e.id)}
                                      disabled={noteSaving || !noteText.trim()}
                                      style={{ background: noteSaving || !noteText.trim() ? "rgba(155,92,246,0.1)" : "rgba(155,92,246,0.25)", border:`1.5px solid ${noteSaving || !noteText.trim() ? "rgba(155,92,246,0.2)" : "rgba(155,92,246,0.6)"}`, color: noteSaving || !noteText.trim() ? "rgba(192,132,252,0.4)" : "#C084FC", borderRadius:9, padding:"6px 16px", cursor: noteSaving || !noteText.trim() ? "default" : "pointer", fontFamily:"inherit", fontWeight:800, fontSize:"0.8rem", transition:"all 0.18s" }}>
                                      {noteSaving ? "Saving…" : `Save Week ${noteWeek} Note`}
                                    </button>
                                  </div>
                                </div>
                              )}
                            </div>
                          );
                        })()}
                      </div>
                    );
                  });
                })()
            }
          </div>
          );
        })()}

        {/* ════════════════════════════════════
            HISTORY TAB (admin)
        ════════════════════════════════════ */}
        {activeTab === "history" && isAdmin && (() => {
          const histSchoolSessions = histSchool
            ? allSessions.filter((s) => String(s.school_id) === histSchool)
            : [];
          const filtered = historyData.filter((r) => {
            const schoolId = r.enrollments?.sessions?.school_id;
            const sessionId = r.enrollments?.session_id;
            if (histSchool && String(schoolId) !== histSchool) return false;
            if (histSession && String(sessionId) !== histSession) return false;
            return true;
          });
          return (
          <div>
            {/* Filters */}
            <div style={{ display:"flex", gap:"0.85rem", flexWrap:"wrap", marginBottom:"1.6rem", alignItems:"flex-end" }}>
              <div>
                <div style={{ fontSize:"0.75rem", fontWeight:700, color:"rgba(255,255,255,0.4)", textTransform:"uppercase", letterSpacing:"0.05em", marginBottom:5 }}>School</div>
                <select className="admin-filter-select" value={histSchool} onChange={(e) => { setHistSchool(e.target.value); setHistSession(""); }}
                  style={{ background:"rgba(10,13,50,0.95)", border:"1.5px solid rgba(255,255,255,0.15)", borderRadius:10, padding:"8px 14px", color:"#fff", fontFamily:"inherit", fontWeight:700, fontSize:"0.84rem", cursor:"pointer", outline:"none", minWidth:180 }}>
                  <option value="">All Schools</option>
                  {schools.map((s) => <option key={s.id} value={String(s.id)}>{s.name}</option>)}
                </select>
              </div>
              <div>
                <div style={{ fontSize:"0.75rem", fontWeight:700, color:"rgba(255,255,255,0.4)", textTransform:"uppercase", letterSpacing:"0.05em", marginBottom:5 }}>Session</div>
                <select className="admin-filter-select" value={histSession} onChange={(e) => setHistSession(e.target.value)}
                  style={{ background:"rgba(10,13,50,0.95)", border:"1.5px solid rgba(255,255,255,0.15)", borderRadius:10, padding:"8px 14px", color:"#fff", fontFamily:"inherit", fontWeight:700, fontSize:"0.84rem", cursor:"pointer", outline:"none", minWidth:200 }}
                  disabled={!histSchool}>
                  <option value="">All Sessions</option>
                  {histSchoolSessions.map((s) => <option key={s.id} value={String(s.id)}>{s.title.replace("Mars Academy — ","")}</option>)}
                </select>
              </div>
              <div style={{ marginLeft:"auto", fontSize:"0.8rem", fontWeight:700, color:"rgba(255,255,255,0.3)", alignSelf:"center" }}>
                {historyLoaded && `${filtered.length} record${filtered.length !== 1 ? "s" : ""}`}
              </div>
            </div>

            {historyLoading
              ? <div style={{ textAlign:"center", padding:"3rem", color:"rgba(255,255,255,0.35)", fontWeight:600 }}>Loading history…</div>
              : filtered.length === 0
              ? <div style={{ textAlign:"center", padding:"4rem 2rem", background:"rgba(255,255,255,0.03)", border:"1.5px dashed rgba(155,92,246,0.25)", borderRadius:20 }}>
                  <div style={{ fontSize:"2.5rem", marginBottom:"0.8rem" }}>📜</div>
                  <div style={{ fontFamily:"'Fredoka One',cursive", fontSize:"1.2rem", color:"rgba(255,255,255,0.5)" }}>No attendance edits recorded yet</div>
                  <p style={{ color:"rgba(255,255,255,0.3)", fontSize:"0.84rem", fontWeight:600, marginTop:"0.5rem" }}>Edits will appear here after teachers mark attendance.</p>
                </div>
              : <div style={{ display:"flex", flexDirection:"column", gap:"0.55rem" }}>
                  {filtered.map((r) => {
                    const student    = r.enrollments?.students;
                    const session    = r.enrollments?.sessions;
                    const reporter   = r.reporter;
                    const schoolName = schools.find((sc) => String(sc.id) === String(session?.school_id))?.name ?? "—";
                    const when       = r.updated_at ? new Date(r.updated_at) : null;
                    const dateStr    = when ? when.toLocaleDateString("en-US", { month:"short", day:"numeric", year:"numeric" }) : "—";
                    const timeStr    = when ? when.toLocaleTimeString("en-US", { hour:"numeric", minute:"2-digit" }) : "";
                    const isNote     = r._type === "note";
                    const accentColor = reporter?.role === "admin" ? "#F59E0B" : "#60A5FA";
                    const avatarBg    = reporter?.role === "admin" ? "rgba(245,158,11,0.15)" : isNote ? "rgba(155,92,246,0.15)" : "rgba(59,130,246,0.15)";
                    const avatarBorder= reporter?.role === "admin" ? "rgba(245,158,11,0.35)" : isNote ? "rgba(155,92,246,0.4)" : "rgba(59,130,246,0.35)";
                    return (
                      <div key={`${r._type}-${r.id}`} style={{ background: isNote ? "rgba(155,92,246,0.05)" : "rgba(255,255,255,0.04)", border:`1.5px solid ${isNote ? "rgba(155,92,246,0.18)" : "rgba(255,255,255,0.08)"}`, borderRadius:14, padding:"0.85rem 1.1rem" }}>
                        <div style={{ display:"flex", alignItems:"center", gap:"1rem", flexWrap:"wrap" }}>
                          {/* Avatar */}
                          <div style={{ width:36, height:36, borderRadius:10, background:avatarBg, border:`1.5px solid ${avatarBorder}`, display:"flex", alignItems:"center", justifyContent:"center", fontSize:"1rem", flexShrink:0 }}>
                            {isNote ? "📝" : reporter?.role === "admin" ? "🛡️" : "👩‍🏫"}
                          </div>
                          {/* Description */}
                          <div style={{ flex:1, minWidth:0 }}>
                            <div style={{ fontSize:"0.88rem", fontWeight:700, marginBottom:3 }}>
                              <span style={{ color: accentColor }}>{reporter?.name ?? "Unknown"}</span>
                              {isNote ? (
                                <>{" "}added a{" "}<span style={{ color:"#C084FC" }}>Week {r.week_number} note</span>{" "}for{" "}<span style={{ color:"#fff" }}>{student?.name ?? "—"}</span></>
                              ) : (
                                <>{" "}marked{" "}<span style={{ color:"#FFD93D" }}>Week {r.week_number}</span>{" "}as{" "}<span style={{ color: r.attended ? "#22C55E" : "#FF6B6B", fontWeight:800 }}>{r.attended ? "✓ Present" : "– Absent"}</span>{" "}for{" "}<span style={{ color:"#fff" }}>{student?.name ?? "—"}</span></>
                              )}
                            </div>
                            <div style={{ fontSize:"0.75rem", color:"rgba(255,255,255,0.35)", fontWeight:600 }}>
                              {schoolName}{session ? ` · ${session.title.replace("Mars Academy — ","")}` : ""}
                            </div>
                            {isNote && r.note && (
                              <div style={{ marginTop:6, background:"rgba(155,92,246,0.1)", border:"1px solid rgba(155,92,246,0.2)", borderRadius:8, padding:"0.45rem 0.7rem", fontSize:"0.82rem", fontWeight:600, color:"rgba(255,255,255,0.75)", lineHeight:1.45, fontStyle:"italic" }}>
                                "{r.note}"
                              </div>
                            )}
                          </div>
                          {/* Timestamp */}
                          <div style={{ textAlign:"right", flexShrink:0, marginLeft:"auto" }}>
                            <div style={{ fontSize:"0.78rem", fontWeight:700, color:"rgba(255,255,255,0.55)" }}>{dateStr}</div>
                            <div style={{ fontSize:"0.72rem", color:"rgba(255,255,255,0.3)", fontWeight:600 }}>{timeStr}</div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
            }
          </div>
          );
        })()}

        {/* ════════════════════════════════════
            PROMOS TAB (admin)
        ════════════════════════════════════ */}
        {activeTab === "promos" && isAdmin && (
          <div>
            <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:"1.2rem", flexWrap:"wrap", gap:"0.75rem" }}>
              <div style={{ fontFamily:"'Fredoka One',cursive", fontSize:"1.2rem" }}>
                Promo Codes ({promoLoading ? "…" : promoCodes.length})
              </div>
              <Btn onClick={() => { setPromoError(""); setShowAddPromo(true); }} variant="success" style={{ padding:"8px 20px", fontSize:"0.85rem" }}>+ New Promo Code</Btn>
            </div>

            {promoLoading
              ? <div style={{ textAlign:"center", padding:"3rem", color:"rgba(255,255,255,0.35)", fontWeight:600 }}>Loading promo codes…</div>
              : promoCodes.length === 0
                ? <div style={{ textAlign:"center", padding:"4rem 2rem", background:"rgba(255,255,255,0.03)", border:"1.5px dashed rgba(34,197,94,0.25)", borderRadius:20 }}>
                    <div style={{ fontSize:"2.5rem", marginBottom:"0.8rem" }}>🎟️</div>
                    <div style={{ fontFamily:"'Fredoka One',cursive", fontSize:"1.2rem", color:"rgba(255,255,255,0.5)" }}>No promo codes yet</div>
                    <p style={{ color:"rgba(255,255,255,0.3)", fontSize:"0.84rem", fontWeight:600, marginTop:"0.5rem" }}>Create a code to offer parents a discount at checkout.</p>
                  </div>
                : <div style={{ display:"flex", flexDirection:"column", gap:"0.75rem" }}>
                    {promoCodes.map((p) => {
                      const isUnlimited = p.max_uses === null;
                      const usageLabel  = isUnlimited ? `${p.current_uses} used · unlimited` : `${p.current_uses} / ${p.max_uses} used`;
                      const usagePct    = isUnlimited ? 0 : Math.round((p.current_uses / p.max_uses) * 100);
                      const maxed       = !isUnlimited && p.current_uses >= p.max_uses;
                      const locked      = maxed || !p.active;
                      return (
                        <div key={p.id} style={{ background:"rgba(255,255,255,0.04)", border:`1.5px solid ${maxed ? "rgba(255,107,107,0.25)" : p.active ? "rgba(34,197,94,0.2)" : "rgba(255,255,255,0.08)"}`, borderRadius:16, padding:"1rem 1.4rem", display:"flex", flexDirection:"column", gap:"0.6rem", opacity: locked ? 0.65 : 1 }}>
                          {/* Top row: code badge + controls */}
                          <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", gap:"0.75rem" }}>
                            <div style={{ background: maxed ? "rgba(255,107,107,0.1)" : "rgba(34,197,94,0.1)", border:`1.5px solid ${maxed ? "rgba(255,107,107,0.3)" : "rgba(34,197,94,0.3)"}`, borderRadius:10, padding:"5px 12px", fontFamily:"'Fredoka One',cursive", fontSize:"0.95rem", color: maxed ? "#FF6B6B" : "#22C55E", letterSpacing:"0.08em" }}>
                              {maxed ? "🔒 " : ""}{p.code}
                            </div>
                            <div style={{ display:"flex", gap:6, alignItems:"center", flexShrink:0 }}>
                              {maxed
                                ? <span style={{ fontSize:"0.76rem", fontWeight:700, color:"rgba(255,107,107,0.6)", padding:"4px 10px", background:"rgba(255,107,107,0.07)", border:"1.5px solid rgba(255,107,107,0.2)", borderRadius:8 }}>Locked</span>
                                : <button
                                    onClick={() => togglePromoActive(p.id, p.active)}
                                    style={{ background: p.active ? "rgba(34,197,94,0.12)" : "rgba(255,107,107,0.12)", border:`1.5px solid ${p.active ? "rgba(34,197,94,0.35)" : "rgba(255,107,107,0.35)"}`, color: p.active ? "#22C55E" : "#FF6B6B", borderRadius:8, padding:"4px 10px", cursor:"pointer", fontFamily:"inherit", fontWeight:700, fontSize:"0.76rem", whiteSpace:"nowrap" }}>
                                    {p.active ? "✓ Active" : "✕ Inactive"}
                                  </button>
                              }
                              <button
                                onClick={() => deletePromo(p.id)}
                                style={{ background:"rgba(255,107,107,0.08)", border:"1.5px solid rgba(255,107,107,0.25)", color:"rgba(255,107,107,0.7)", borderRadius:8, padding:"4px 10px", cursor:"pointer", fontFamily:"inherit", fontWeight:700, fontSize:"0.76rem" }}>
                                Delete
                              </button>
                            </div>
                          </div>
                          {/* Info row */}
                          <div>
                            <div style={{ display:"flex", gap:8, alignItems:"center", flexWrap:"wrap", marginBottom:3 }}>
                              <span style={{ fontWeight:800, fontSize:"0.92rem", color:"#FFD93D" }}>-${p.discount_amount} off</span>
                              {p.description && <span style={{ color:"rgba(255,255,255,0.45)", fontSize:"0.82rem", fontWeight:600 }}>{p.description}</span>}
                            </div>
                            <div style={{ display:"flex", gap:8, alignItems:"center", flexWrap:"wrap" }}>
                              <span style={{ fontSize:"0.77rem", fontWeight:600, color: maxed ? "rgba(255,107,107,0.7)" : "rgba(255,255,255,0.4)" }}>{usageLabel}</span>
                              {!isUnlimited && (
                                <div style={{ width:80, height:4, borderRadius:4, background:"rgba(255,255,255,0.08)", overflow:"hidden" }}>
                                  <div style={{ height:"100%", width:`${usagePct}%`, background: maxed ? "#FF6B6B" : "#22C55E", borderRadius:4, transition:"width 0.4s" }} />
                                </div>
                              )}
                              {maxed && <span style={{ fontSize:"0.72rem", fontWeight:800, color:"#FF6B6B", background:"rgba(255,107,107,0.12)", border:"1px solid rgba(255,107,107,0.3)", borderRadius:5, padding:"1px 7px" }}>🔒 Limit reached</span>}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
            }
          </div>
        )}

      </div>

      {/* ── ADD PROMO MODAL ── */}
      {showAddPromo && (
        <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.78)", zIndex:300, display:"flex", alignItems:"center", justifyContent:"center", padding:"1rem" }}>
          <div style={{ background:"rgba(10,13,50,0.98)", border:"1.5px solid rgba(34,197,94,0.3)", borderRadius:24, padding:"2.2rem", maxWidth:440, width:"100%", backdropFilter:"blur(20px)" }}>
            <div style={{ fontFamily:"'Fredoka One',cursive", fontSize:"1.5rem", marginBottom:"1.2rem" }}>🎟️ New Promo Code</div>
            <div style={{ display:"flex", flexDirection:"column", gap:"0.85rem" }}>
              <FormInput
                label="Promo Code"
                value={newPromo.code}
                onChange={(e) => setNewPromo((p) => ({ ...p, code: e.target.value.toUpperCase() }))}
                placeholder="SUMMER25"
              />
              <FormInput
                label="Description (optional)"
                value={newPromo.description}
                onChange={(e) => setNewPromo((p) => ({ ...p, description: e.target.value }))}
                placeholder="e.g. Summer 2025 family discount"
              />
              <FormInput
                label="Discount Amount ($)"
                type="number"
                value={newPromo.discount_amount}
                onChange={(e) => setNewPromo((p) => ({ ...p, discount_amount: e.target.value }))}
                placeholder="25"
              />

              {/* Usage limit */}
              <div>
                <div style={{ fontSize:"0.78rem", fontWeight:700, color:"rgba(255,255,255,0.45)", textTransform:"uppercase", letterSpacing:"0.05em", marginBottom:8 }}>Usage Limit</div>
                <label style={{ display:"flex", alignItems:"center", gap:"0.6rem", cursor:"pointer", padding:"0.65rem 0.85rem", background:"rgba(34,197,94,0.07)", border:"1.5px solid rgba(34,197,94,0.2)", borderRadius:10, marginBottom: newPromo.unlimited ? 0 : "0.6rem" }}>
                  <input
                    type="checkbox"
                    checked={newPromo.unlimited}
                    onChange={(e) => setNewPromo((p) => ({ ...p, unlimited: e.target.checked, max_uses: "" }))}
                    style={{ width:16, height:16, accentColor:"#22C55E", cursor:"pointer", flexShrink:0 }}
                  />
                  <span style={{ fontSize:"0.84rem", fontWeight:700, color:"rgba(255,255,255,0.8)" }}>Unlimited uses</span>
                </label>
                {!newPromo.unlimited && (
                  <FormInput
                    label="Max Uses"
                    type="number"
                    value={newPromo.max_uses}
                    onChange={(e) => setNewPromo((p) => ({ ...p, max_uses: e.target.value }))}
                    placeholder="e.g. 10"
                  />
                )}
              </div>

              {promoError && <p style={{ color:"#FF6B6B", fontSize:"0.82rem", fontWeight:700, margin:0 }}>{promoError}</p>}
              <div style={{ display:"flex", gap:"0.7rem", marginTop:"0.3rem" }}>
                <Btn onClick={() => { setShowAddPromo(false); setPromoError(""); setNewPromo({ code:"", description:"", discount_amount:"", max_uses:"", unlimited:true }); }} variant="ghost" style={{ flex:"0 0 auto" }}>Cancel</Btn>
                <Btn onClick={handleAddPromo} variant="success" style={{ flex:1 }} disabled={promoSaving}>{promoSaving ? "Saving…" : "Create Code ✓"}</Btn>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── CONTACT LIST MODAL ── */}
      {showContactList && parentSessionFilter && (() => {
        const sessionInfo = adminSessions.find((s) => String(s.id) === parentSessionFilter);
        const contacts = parents.flatMap((p) => {
          const matchingStudents = (p.students ?? []).filter((st) =>
            (st.enrollments ?? []).some((e) => String(e.session_id) === parentSessionFilter)
          );
          return matchingStudents.map((st) => ({ parentName: p.name, email: p.email, studentName: st.name }));
        });
        const allEmails = [...new Set(contacts.map((c) => c.email).filter(Boolean))].join(", ");
        const allNames  = contacts.map((c) => c.studentName).filter(Boolean).join(", ");
        return (
          <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.82)", zIndex:300, display:"flex", alignItems:"center", justifyContent:"center", padding:"1rem" }} onClick={() => setShowContactList(false)}>
            <div onClick={(e) => e.stopPropagation()} style={{ background:"rgba(10,13,50,0.98)", border:"1.5px solid rgba(59,130,246,0.3)", borderRadius:24, padding:"2rem", maxWidth:520, width:"100%", backdropFilter:"blur(20px)", maxHeight:"88vh", overflowY:"auto" }}>

              {/* Header */}
              <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:"1.2rem" }}>
                <div>
                  <div style={{ fontFamily:"'Fredoka One',cursive", fontSize:"1.4rem" }}>📋 Contact List</div>
                  <div style={{ color:"rgba(255,255,255,0.4)", fontSize:"0.82rem", fontWeight:600, marginTop:3 }}>
                    {sessionInfo?.title?.replace("Mars Academy — ","") ?? "Session"} · {contacts.length} student{contacts.length !== 1 ? "s" : ""}
                  </div>
                </div>
                <button onClick={() => setShowContactList(false)} style={{ background:"rgba(255,255,255,0.07)", border:"none", borderRadius:8, color:"rgba(255,255,255,0.5)", cursor:"pointer", fontSize:"1.1rem", padding:"4px 10px", fontFamily:"inherit" }}>✕</button>
              </div>

              {/* Copy all emails */}
              <div style={{ background:"rgba(59,130,246,0.07)", border:"1.5px solid rgba(59,130,246,0.2)", borderRadius:12, padding:"0.75rem 1rem", marginBottom:"0.75rem" }}>
                <div style={{ fontSize:"0.72rem", fontWeight:700, color:"rgba(255,255,255,0.35)", textTransform:"uppercase", letterSpacing:"0.05em", marginBottom:6 }}>All Emails</div>
                <div style={{ fontSize:"0.82rem", fontWeight:600, color:"rgba(255,255,255,0.7)", wordBreak:"break-all", lineHeight:1.6, marginBottom:"0.75rem" }}>
                  {allEmails || "—"}
                </div>
                <button
                  onClick={() => { navigator.clipboard.writeText(allEmails); setEmailCopied(true); }}
                  style={{ background: emailCopied ? "rgba(34,197,94,0.15)" : "rgba(59,130,246,0.2)", border:`1.5px solid ${emailCopied ? "rgba(34,197,94,0.4)" : "rgba(59,130,246,0.4)"}`, color: emailCopied ? "#22C55E" : "#60A5FA", borderRadius:8, padding:"6px 14px", cursor:"pointer", fontFamily:"inherit", fontWeight:700, fontSize:"0.8rem", transition:"all 0.2s" }}>
                  {emailCopied ? "✓ Copied!" : "Copy All Emails"}
                </button>
              </div>

              {/* Copy all student names */}
              <div style={{ background:"rgba(255,217,61,0.06)", border:"1.5px solid rgba(255,217,61,0.18)", borderRadius:12, padding:"0.75rem 1rem", marginBottom:"1.2rem" }}>
                <div style={{ fontSize:"0.72rem", fontWeight:700, color:"rgba(255,255,255,0.35)", textTransform:"uppercase", letterSpacing:"0.05em", marginBottom:6 }}>All Student Names</div>
                <div style={{ fontSize:"0.82rem", fontWeight:600, color:"rgba(255,255,255,0.7)", wordBreak:"break-all", lineHeight:1.6, marginBottom:"0.75rem" }}>
                  {allNames || "—"}
                </div>
                <button
                  onClick={() => { navigator.clipboard.writeText(allNames); setNamesCopied(true); }}
                  style={{ background: namesCopied ? "rgba(34,197,94,0.15)" : "rgba(255,217,61,0.12)", border:`1.5px solid ${namesCopied ? "rgba(34,197,94,0.4)" : "rgba(255,217,61,0.3)"}`, color: namesCopied ? "#22C55E" : "#FFD93D", borderRadius:8, padding:"6px 14px", cursor:"pointer", fontFamily:"inherit", fontWeight:700, fontSize:"0.8rem", transition:"all 0.2s" }}>
                  {namesCopied ? "✓ Copied!" : "Copy All Names"}
                </button>
              </div>

              {/* Per-student rows */}
              <div style={{ display:"flex", flexDirection:"column", gap:"0.55rem" }}>
                {contacts.map((c, i) => (
                  <div key={i} style={{ display:"flex", justifyContent:"space-between", alignItems:"center", background:"rgba(255,255,255,0.04)", border:"1.5px solid rgba(255,255,255,0.08)", borderRadius:12, padding:"0.7rem 1rem", gap:"1rem" }}>
                    <div>
                      <div style={{ fontWeight:800, fontSize:"0.88rem" }}>🧑‍🚀 {c.studentName}</div>
                      <div style={{ color:"rgba(255,255,255,0.4)", fontSize:"0.78rem", fontWeight:600, marginTop:2 }}>{c.parentName}</div>
                    </div>
                    <div style={{ fontSize:"0.82rem", fontWeight:600, color:"#60A5FA", textAlign:"right", wordBreak:"break-all" }}>{c.email}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        );
      })()}

      {/* ── STUDENT DETAIL MODAL ── */}
      {expandedStudent && (
        <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.82)", zIndex:300, display:"flex", alignItems:"center", justifyContent:"center", padding:"1rem" }} onClick={() => setExpandedStudent(null)}>
          <div onClick={(e) => e.stopPropagation()} style={{ background:"rgba(10,13,50,0.98)", border:"1.5px solid rgba(255,217,61,0.25)", borderRadius:24, padding:"2rem", maxWidth:580, width:"100%", backdropFilter:"blur(20px)", maxHeight:"88vh", overflowY:"auto" }}>

            {/* Header */}
            <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:"1.4rem" }}>
              <div>
                <div style={{ fontFamily:"'Fredoka One',cursive", fontSize:"1.5rem", color:"#FFD93D" }}>🧑‍🚀 {expandedStudent.name}</div>
                <div style={{ color:"rgba(255,255,255,0.4)", fontSize:"0.82rem", fontWeight:600, marginTop:3 }}>Parent: {expandedStudent.parentName}</div>
              </div>
              <button onClick={() => setExpandedStudent(null)} style={{ background:"rgba(255,255,255,0.07)", border:"none", borderRadius:8, color:"rgba(255,255,255,0.5)", cursor:"pointer", fontSize:"1.1rem", padding:"4px 10px", fontFamily:"inherit" }}>✕</button>
            </div>

            {/* Student details */}
            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:"0.5rem 1.5rem", background:"rgba(255,255,255,0.03)", borderRadius:14, padding:"0.9rem 1.1rem", marginBottom:"1.4rem" }}>
              {[["Grade", expandedStudent.grade],["Age", expandedStudent.age],["Allergies", expandedStudent.allergies],["Accommodations", expandedStudent.needs]].map(([k, v]) => (
                <div key={k} style={{ display:"flex", justifyContent:"space-between", fontSize:"0.82rem", padding:"0.3rem 0", borderBottom:"1px solid rgba(255,255,255,0.05)" }}>
                  <span style={{ color:"rgba(255,255,255,0.38)", fontWeight:600 }}>{k}</span>
                  <span style={{ fontWeight:700 }}>{v || "—"}</span>
                </div>
              ))}
            </div>

            {/* Enrollments */}
            <div style={{ fontFamily:"'Fredoka One',cursive", fontSize:"1rem", marginBottom:"0.9rem", color:"rgba(255,255,255,0.7)" }}>
              Enrollment History ({(expandedStudent.enrollments ?? []).length})
            </div>

            {(expandedStudent.enrollments ?? []).length === 0
              ? <div style={{ textAlign:"center", padding:"2rem", color:"rgba(255,255,255,0.3)", fontWeight:600 }}>No enrollments yet.</div>
              : <div style={{ display:"flex", flexDirection:"column", gap:"0.9rem" }}>
                  {(expandedStudent.enrollments ?? []).map((en) => {
                    const sd = en.sessions;
                    if (!sd) return null;
                    const schoolName = schools.find((sc) => sc.id === sd.school_id)?.name ?? "—";
                    const weeksPresent = WEEKS.filter((w) => (en.attendance ?? []).some((a) => a.week_number === w && a.attended)).length;
                    const attPct = Math.round((weeksPresent / 6) * 100);
                    const attColor = attPct >= 80 ? "#22C55E" : attPct >= 50 ? "#F59E0B" : attPct > 0 ? "#FF6B6B" : "rgba(255,255,255,0.2)";
                    const enrolledDate = new Date(en.created_at).toLocaleDateString("en-US", { month:"short", day:"numeric", year:"numeric" });
                    return (
                      <div key={en.id} style={{ background:"rgba(255,255,255,0.04)", border:"1.5px solid rgba(155,92,246,0.2)", borderRadius:16, padding:"1.1rem 1.2rem" }}>

                        {/* Session title + payment */}
                        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", gap:8, marginBottom:"0.5rem" }}>
                          <div style={{ fontWeight:800, fontSize:"0.95rem" }}>{sd.title}</div>
                          <Badge color={en.paid ? "#22C55E" : "#F59E0B"} small>{en.paid ? "✓ Paid" : "⏳ Pending"}</Badge>
                        </div>
                        {en.paid && (
                          <div style={{ display:"flex", gap:6, flexWrap:"wrap", marginBottom:"0.5rem" }}>
                            {en.amount_paid != null && (
                              <Badge color="#22C55E" small>💰 ${en.amount_paid}</Badge>
                            )}
                            {en.paid_at && (
                              <Badge color="rgba(34,197,94,0.4)" small>
                                🗓 {new Date(en.paid_at).toLocaleString("en-US", { month:"short", day:"numeric", year:"numeric", hour:"numeric", minute:"2-digit" })}
                              </Badge>
                            )}
                            {(() => {
                              if (sd.price === 0) return <Badge color="#60A5FA" small>🎁 Free Session</Badge>;
                              const parts = [];
                              if (en.promo_code) parts.push(`Promo: ${en.promo_code}`);
                              if (en.sibling_discount) parts.push("Sibling Discount");
                              if (parts.length > 0) return parts.map((p) => <Badge key={p} color="#A78BFA" small>🎟️ {p}</Badge>);
                              if (en.amount_paid != null && en.amount_paid < sd.price) return <Badge color="#A78BFA" small>🎟️ Discounted</Badge>;
                              return <Badge color="#60A5FA" small>💳 Credit Card</Badge>;
                            })()}
                          </div>
                        )}

                        {/* School + schedule */}
                        <div style={{ display:"flex", gap:6, flexWrap:"wrap", marginBottom:"0.75rem" }}>
                          <Badge color="#3B82F6" small>🏫 {schoolName}</Badge>
                          <Badge color="#9B5CF6" small>{sd.days} · {sd.time}</Badge>
                          <Badge color="rgba(255,255,255,0.15)" small>📅 {sd.dates}</Badge>
                          <Badge color="rgba(255,255,255,0.1)" small>Enrolled {enrolledDate}</Badge>
                        </div>

                        {/* Attendance strip */}
                        <div style={{ borderTop:"1px solid rgba(255,255,255,0.07)", paddingTop:"0.75rem" }}>
                          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:"0.5rem" }}>
                            <span style={{ fontSize:"0.7rem", fontWeight:700, color:"rgba(255,255,255,0.35)", textTransform:"uppercase", letterSpacing:"0.05em" }}>Attendance</span>
                            <span style={{ fontSize:"0.75rem", fontWeight:800, color: attColor }}>{weeksPresent}/6 weeks · {attPct}%</span>
                          </div>
                          <WeekDots attendance={en.attendance} />
                        </div>
                      </div>
                    );
                  })}
                </div>
            }
          </div>
        </div>
      )}

      {/* ── ADD SESSION MODAL ── */}
      {showAddSession && (
        <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.78)", zIndex:300, display:"flex", alignItems:"center", justifyContent:"center", padding:"1rem" }}>
          <div style={{ background:"rgba(10,13,50,0.98)", border:"1.5px solid rgba(155,92,246,0.3)", borderRadius:24, padding:"2.2rem", maxWidth:480, width:"100%", backdropFilter:"blur(20px)", maxHeight:"90vh", overflowY:"auto" }}>
            <div style={{ fontFamily:"'Fredoka One',cursive", fontSize:"1.5rem", marginBottom:"1.2rem" }}>📅 New Session</div>
            <div style={{ display:"flex", flexDirection:"column", gap:"0.85rem" }}>

              {/* School toggle */}
              <div>
                <div style={{ fontSize:"0.75rem", fontWeight:700, color:"rgba(255,255,255,0.4)", textTransform:"uppercase", letterSpacing:"0.05em", marginBottom:8 }}>School</div>
                <div style={{ display:"flex", gap:6, marginBottom:"0.75rem" }}>
                  {[false, true].map((isNew) => (
                    <button key={String(isNew)} onClick={() => { setNewSchoolMode(isNew); setSessionError(""); }}
                      style={{ flex:1, padding:"7px 0", borderRadius:9, border:`1.5px solid ${newSchoolMode===isNew ? "rgba(155,92,246,0.6)" : "rgba(255,255,255,0.1)"}`, background: newSchoolMode===isNew ? "rgba(155,92,246,0.15)" : "transparent", color: newSchoolMode===isNew ? "#9B5CF6" : "rgba(255,255,255,0.4)", fontFamily:"inherit", fontWeight:800, fontSize:"0.8rem", cursor:"pointer", transition:"all 0.18s" }}>
                      {isNew ? "➕ New School" : "🏫 Existing School"}
                    </button>
                  ))}
                </div>
                {newSchoolMode
                  ? <div style={{ display:"flex", flexDirection:"column", gap:"0.7rem", background:"rgba(155,92,246,0.06)", border:"1.5px solid rgba(155,92,246,0.2)", borderRadius:12, padding:"0.9rem" }}>
                      <FormInput label="School Name" value={newSchool.name} onChange={(e) => setNewSchool((p) => ({ ...p, name:e.target.value }))} placeholder="Riverside Elementary" />
                      <FormInput label="Location / Address" value={newSchool.location} onChange={(e) => setNewSchool((p) => ({ ...p, location:e.target.value }))} placeholder="123 Main St, City, State" />
                    </div>
                  : <FormSelect value={newSession.school_id} onChange={(e) => setNewSession((p) => ({ ...p, school_id:e.target.value }))} placeholder="Select school..." options={schools.map((s) => ({ value:s.id, label:s.name }))} />
                }
              </div>
              <FormInput label="Session Title" value={newSession.title} onChange={(e) => setNewSession((p) => ({ ...p, title:e.target.value }))} placeholder="Mars Academy — Fall Session" />
              <div style={{ display:"flex", flexWrap:"wrap", gap:"0.85rem" }}>
                <FormInput label="Day(s)" value={newSession.days} onChange={(e) => setNewSession((p) => ({ ...p, days:e.target.value }))} placeholder="Wednesday" half />
                <FormInput label="Time"   value={newSession.time} onChange={(e) => setNewSession((p) => ({ ...p, time:e.target.value }))} placeholder="4:00–6:00 PM" half />
              </div>
              <FormInput label="Date Range" value={newSession.dates} onChange={(e) => setNewSession((p) => ({ ...p, dates:e.target.value }))} placeholder="Sep 3 – Oct 29" />
              <div style={{ display:"flex", flexWrap:"wrap", gap:"0.85rem" }}>
                <FormInput label="Spots"    type="number" value={newSession.spots} onChange={(e) => setNewSession((p) => ({ ...p, spots:e.target.value }))} placeholder="10" half />
                <FormInput label="Price ($) — 0 for free" type="number" value={newSession.price} onChange={(e) => setNewSession((p) => ({ ...p, price:e.target.value }))} placeholder="249" half />
              </div>
              {sessionError && <p style={{ color:"#FF6B6B", fontSize:"0.82rem", fontWeight:700 }}>{sessionError}</p>}
              <div style={{ display:"flex", gap:"0.7rem", marginTop:"0.3rem" }}>
                <Btn onClick={() => { setShowAddSession(false); setNewSchoolMode(false); setNewSchool({ name:"", location:"" }); setSessionError(""); }} variant="ghost" style={{ flex:"0 0 auto" }}>Cancel</Btn>
                <Btn onClick={handleAddSession} variant="purple" style={{ flex:1 }} disabled={sessionSaving}>{sessionSaving ? "Saving…" : newSchoolMode ? "Create School & Session ✓" : "Add Session ✓"}</Btn>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── EDIT SESSION MODAL ── */}
      {editingSession && (
        <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.78)", zIndex:300, display:"flex", alignItems:"center", justifyContent:"center", padding:"1rem" }}>
          <div style={{ background:"rgba(10,13,50,0.98)", border:"1.5px solid rgba(155,92,246,0.3)", borderRadius:24, padding:"2.2rem", maxWidth:480, width:"100%", backdropFilter:"blur(20px)" }}>
            <div style={{ fontFamily:"'Fredoka One',cursive", fontSize:"1.5rem", marginBottom:"1.2rem" }}>✏️ Edit Session</div>
            <div style={{ display:"flex", flexDirection:"column", gap:"0.85rem" }}>
              <FormInput label="Session Title" value={editingSession.title} onChange={(e) => setEditingSession((p) => ({ ...p, title:e.target.value }))} />
              <div style={{ display:"flex", flexWrap:"wrap", gap:"0.85rem" }}>
                <FormInput label="Day(s)" value={editingSession.days} onChange={(e) => setEditingSession((p) => ({ ...p, days:e.target.value }))} half />
                <FormInput label="Time"   value={editingSession.time} onChange={(e) => setEditingSession((p) => ({ ...p, time:e.target.value }))} half />
              </div>
              <FormInput label="Date Range" value={editingSession.dates} onChange={(e) => setEditingSession((p) => ({ ...p, dates:e.target.value }))} />
              <div style={{ display:"flex", flexWrap:"wrap", gap:"0.85rem" }}>
                <FormInput label="Spots"    type="number" value={editingSession.spots} onChange={(e) => setEditingSession((p) => ({ ...p, spots:e.target.value }))} half />
                <FormInput label="Price ($)" type="number" value={editingSession.price} onChange={(e) => setEditingSession((p) => ({ ...p, price:e.target.value }))} half />
              </div>
              {sessionError && <p style={{ color:"#FF6B6B", fontSize:"0.82rem", fontWeight:700 }}>{sessionError}</p>}
              <div style={{ display:"flex", gap:"0.7rem", marginTop:"0.3rem" }}>
                <Btn onClick={() => setEditingSession(null)} variant="ghost" style={{ flex:"0 0 auto" }}>Cancel</Btn>
                <Btn onClick={handleEditSession} variant="purple" style={{ flex:1 }} disabled={sessionSaving}>{sessionSaving ? "Saving…" : "Save Changes ✓"}</Btn>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── ADD STAFF MODAL ── */}
      {showAddStaff && (
        <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.78)", zIndex:300, display:"flex", alignItems:"center", justifyContent:"center", padding:"1rem" }}>
          <div style={{ background:"rgba(10,13,50,0.98)", border:"1.5px solid rgba(59,130,246,0.3)", borderRadius:24, padding:"2.2rem", maxWidth:440, width:"100%", backdropFilter:"blur(20px)" }}>
            <div style={{ fontFamily:"'Fredoka One',cursive", fontSize:"1.5rem", marginBottom:"1.2rem" }}>👥 Add Staff Member</div>
            {staffAdded
              ? <div style={{ textAlign:"center", padding:"1.5rem 0" }}>
                  <div style={{ fontSize:"2.5rem", marginBottom:"0.75rem" }}>✅</div>
                  <div style={{ fontWeight:800, color:"#22C55E", marginBottom:6 }}>Account created!</div>
                  <div style={{ color:"rgba(255,255,255,0.5)", fontSize:"0.85rem", fontWeight:600, marginBottom:"1.2rem" }}>Share the credentials with {newStaff.name}.</div>
                  <Btn onClick={() => { setShowAddStaff(false); setStaffAdded(false); setNewStaff({ name:"", email:"", password:"", role:"teacher", school_id:"" }); setAdminRoleConfirmed(false); }} variant="ghost">Done</Btn>
                </div>
              : <div style={{ display:"flex", flexDirection:"column", gap:"0.85rem" }}>
                  <div style={{ display:"flex", flexWrap:"wrap", gap:"0.85rem" }}>
                    <FormInput label="Full Name" value={newStaff.name} onChange={(e) => setNewStaff((p) => ({ ...p, name:e.target.value }))} placeholder="Ms. Jane Smith" required half />
                    <FormInput label="Email" type="email" value={newStaff.email} onChange={(e) => setNewStaff((p) => ({ ...p, email:e.target.value }))} placeholder="jane@marsacademy.io" required half />
                  </div>
                  <div>
                    <FormInput label="Temporary Password" type="password" value={newStaff.password} onChange={(e) => setNewStaff((p) => ({ ...p, password:e.target.value }))} placeholder="Create a temporary password" required />
                    {newStaff.password.length > 0 && <PasswordChecklist password={newStaff.password} />}
                  </div>
                  <FormSelect label="Role" value={newStaff.role} onChange={(e) => { setNewStaff((p) => ({ ...p, role:e.target.value, school_id:"" })); setAdminRoleConfirmed(false); }} options={[{ value:"teacher", label:"Teacher" },{ value:"admin", label:"Admin" }]} />
                  {newStaff.role === "admin" && (
                    <div style={{ background:"rgba(245,158,11,0.1)", border:"1.5px solid rgba(245,158,11,0.45)", borderRadius:12, padding:"0.85rem 1rem" }}>
                      <div style={{ fontWeight:800, color:"#F59E0B", fontSize:"0.88rem", marginBottom:"0.55rem" }}>⚠️ Admin Access Warning</div>
                      <div style={{ color:"rgba(255,255,255,0.65)", fontSize:"0.81rem", fontWeight:600, lineHeight:1.55, marginBottom:"0.75rem" }}>
                        Admins have full access to all data, staff accounts, and settings. Only grant this role to trusted personnel.
                      </div>
                      <label style={{ display:"flex", alignItems:"center", gap:"0.6rem", cursor:"pointer" }}>
                        <input
                          type="checkbox"
                          checked={adminRoleConfirmed}
                          onChange={(e) => setAdminRoleConfirmed(e.target.checked)}
                          style={{ width:16, height:16, accentColor:"#F59E0B", cursor:"pointer", flexShrink:0 }}
                        />
                        <span style={{ fontSize:"0.82rem", fontWeight:700, color:"rgba(255,255,255,0.8)" }}>
                          I understand — grant admin access to <span style={{ color:"#F59E0B" }}>{newStaff.name || "this user"}</span>
                        </span>
                      </label>
                    </div>
                  )}
                  {staffCreateError && <p style={{ color:"#FF6B6B", fontSize:"0.82rem", fontWeight:700 }}>{staffCreateError}</p>}
                  <div style={{ display:"flex", gap:"0.7rem", marginTop:"0.3rem" }}>
                    <Btn onClick={() => setShowAddStaff(false)} variant="ghost" style={{ flex:"0 0 auto" }}>Cancel</Btn>
                    <Btn onClick={handleCreateStaff} variant="success" style={{ flex:1 }} disabled={staffCreating || !isPasswordStrong(newStaff.password) || (newStaff.role === "admin" && !adminRoleConfirmed)}>{staffCreating ? "Creating…" : "Create Account ✓"}</Btn>
                  </div>
                </div>
            }
          </div>
        </div>
      )}

      {/* ── EDIT STAFF MODAL ── */}
      {editingStaff && (
        <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.78)", zIndex:300, display:"flex", alignItems:"center", justifyContent:"center", padding:"1rem" }}>
          <div style={{ background:"rgba(10,13,50,0.98)", border:"1.5px solid rgba(155,92,246,0.3)", borderRadius:24, padding:"2.2rem", maxWidth:420, width:"100%", backdropFilter:"blur(20px)" }}>
            <div style={{ fontFamily:"'Fredoka One',cursive", fontSize:"1.5rem", marginBottom:"1.4rem" }}>✏️ Edit Staff Member</div>
            <div style={{ display:"flex", flexDirection:"column", gap:"0.85rem" }}>

              {/* Name */}
              <FormInput
                label="Full Name"
                value={editingStaff.name}
                onChange={(e) => setEditingStaff((p) => ({ ...p, name:e.target.value }))}
                placeholder="Ms. Jane Smith"
                required
              />

              {/* Email — read-only, requires Edge Function to change */}
              <div>
                <div style={{ fontSize:"0.78rem", fontWeight:700, color:"rgba(255,255,255,0.45)", textTransform:"uppercase", letterSpacing:"0.05em", marginBottom:5 }}>Email <span style={{ fontWeight:600, color:"rgba(255,255,255,0.28)", textTransform:"none", letterSpacing:0 }}>(contact support to change)</span></div>
                <div style={{ background:"rgba(255,255,255,0.04)", border:"1.5px solid rgba(255,255,255,0.1)", borderRadius:10, padding:"10px 14px", color:"rgba(255,255,255,0.4)", fontSize:"0.88rem", fontWeight:600, wordBreak:"break-all" }}>{editingStaff.email}</div>
              </div>

              {/* Role */}
              <FormSelect
                label="Role"
                value={editingStaff.role}
                onChange={(e) => { setEditingStaff((p) => ({ ...p, role:e.target.value })); setEditAdminConfirmed(false); }}
                options={[{ value:"teacher", label:"Teacher" },{ value:"admin", label:"Admin" }]}
              />

              {/* Admin role warning */}
              {editingStaff.role === "admin" && (
                <div style={{ background:"rgba(245,158,11,0.1)", border:"1.5px solid rgba(245,158,11,0.45)", borderRadius:12, padding:"0.85rem 1rem" }}>
                  <div style={{ fontWeight:800, color:"#F59E0B", fontSize:"0.88rem", marginBottom:"0.55rem" }}>⚠️ Admin Access Warning</div>
                  <div style={{ color:"rgba(255,255,255,0.65)", fontSize:"0.81rem", fontWeight:600, lineHeight:1.55, marginBottom:"0.75rem" }}>
                    Admins have full access to all data, staff accounts, and settings.
                  </div>
                  <label style={{ display:"flex", alignItems:"center", gap:"0.6rem", cursor:"pointer" }}>
                    <input
                      type="checkbox"
                      checked={editAdminConfirmed}
                      onChange={(e) => setEditAdminConfirmed(e.target.checked)}
                      style={{ width:16, height:16, accentColor:"#F59E0B", cursor:"pointer", flexShrink:0 }}
                    />
                    <span style={{ fontSize:"0.82rem", fontWeight:700, color:"rgba(255,255,255,0.8)" }}>
                      I understand — grant admin access to <span style={{ color:"#F59E0B" }}>{editingStaff.name || "this user"}</span>
                    </span>
                  </label>
                </div>
              )}

              {staffEditError && <p style={{ color:"#FF6B6B", fontSize:"0.82rem", fontWeight:700, margin:0 }}>{staffEditError}</p>}

              <div style={{ display:"flex", gap:"0.7rem", marginTop:"0.3rem" }}>
                <Btn onClick={() => { setEditingStaff(null); setEditAdminConfirmed(false); setStaffEditError(""); }} variant="ghost" style={{ flex:"0 0 auto" }}>Cancel</Btn>
                <Btn onClick={handleEditStaff} variant="purple" style={{ flex:1 }} disabled={staffEditSaving || (editingStaff.role === "admin" && !editAdminConfirmed)}>
                  {staffEditSaving ? "Saving…" : "Save Changes ✓"}
                </Btn>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── SESSION SIGNUPS MODAL ── */}
      {viewingSessionSignups && (() => {
        const s = viewingSessionSignups;
        const signups = parents.flatMap((p) =>
          (p.students ?? []).flatMap((st) =>
            (st.enrollments ?? [])
              .filter((e) => e.session_id === s.id)
              .map((e) => ({ studentName: st.name, grade: st.grade, parentName: p.name, parentEmail: p.email, paid: e.paid, amount_paid: e.amount_paid, paid_at: e.paid_at, promo_code: e.promo_code, sibling_discount: e.sibling_discount, sessionPrice: s.price }))
          )
        );
        const paidCount = signups.filter((r) => r.paid).length;
        const allEmails = [...new Set(signups.map((r) => r.parentEmail).filter(Boolean))].join(", ");
        const allNames  = signups.map((r) => r.studentName).filter(Boolean).join(", ");
        return (
          <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.78)", zIndex:300, display:"flex", alignItems:"center", justifyContent:"center", padding:"1rem" }}>
            <div style={{ background:"rgba(10,13,50,0.98)", border:"1.5px solid rgba(34,197,94,0.3)", borderRadius:24, padding:"2.2rem", maxWidth:540, width:"100%", backdropFilter:"blur(20px)", maxHeight:"85vh", display:"flex", flexDirection:"column" }}>
              {/* Header */}
              <div style={{ marginBottom:"0.4rem" }}>
                <div style={{ fontFamily:"'Fredoka One',cursive", fontSize:"1.4rem" }}>👥 Sign-up List</div>
                <div style={{ fontWeight:700, fontSize:"0.88rem", color:"rgba(255,255,255,0.55)", marginTop:3 }}>{s.title}</div>
                <div style={{ fontSize:"0.78rem", fontWeight:600, color:"rgba(255,255,255,0.35)", marginTop:2 }}>{s.schoolName} · {s.days} · {s.dates}</div>
              </div>
              {/* Summary badges */}
              <div style={{ display:"flex", gap:6, flexWrap:"wrap", margin:"0.85rem 0" }}>
                <Badge color="#22C55E" small>{signups.length} enrolled</Badge>
                <Badge color="#22C55E" small>{paidCount} paid</Badge>
                {signups.length - paidCount > 0 && <Badge color="#F59E0B" small>{signups.length - paidCount} unpaid</Badge>}
              </div>
              {/* Copy strips — only show when there are signups */}
              {signups.length > 0 && (
                <div style={{ display:"flex", flexDirection:"column", gap:"0.5rem", marginBottom:"0.85rem" }}>
                  {/* Emails */}
                  <div style={{ background:"rgba(59,130,246,0.07)", border:"1.5px solid rgba(59,130,246,0.2)", borderRadius:12, padding:"0.65rem 0.9rem", display:"flex", alignItems:"center", gap:"0.75rem", flexWrap:"wrap" }}>
                    <div style={{ flex:1, minWidth:0 }}>
                      <div style={{ fontSize:"0.68rem", fontWeight:700, color:"rgba(255,255,255,0.3)", textTransform:"uppercase", letterSpacing:"0.05em", marginBottom:3 }}>Parent Emails</div>
                      <div style={{ fontSize:"0.78rem", fontWeight:600, color:"rgba(255,255,255,0.6)", wordBreak:"break-all", lineHeight:1.5 }}>{allEmails || "—"}</div>
                    </div>
                    <button
                      onClick={() => { navigator.clipboard.writeText(allEmails); setSessionListEmailCopied(true); setTimeout(() => setSessionListEmailCopied(false), 2000); }}
                      style={{ background: sessionListEmailCopied ? "rgba(34,197,94,0.15)" : "rgba(59,130,246,0.2)", border:`1.5px solid ${sessionListEmailCopied ? "rgba(34,197,94,0.4)" : "rgba(59,130,246,0.4)"}`, color: sessionListEmailCopied ? "#22C55E" : "#60A5FA", borderRadius:8, padding:"5px 12px", cursor:"pointer", fontFamily:"inherit", fontWeight:700, fontSize:"0.75rem", whiteSpace:"nowrap", transition:"all 0.2s" }}>
                      {sessionListEmailCopied ? "✓ Copied!" : "Copy Emails"}
                    </button>
                  </div>
                  {/* Names */}
                  <div style={{ background:"rgba(255,217,61,0.06)", border:"1.5px solid rgba(255,217,61,0.18)", borderRadius:12, padding:"0.65rem 0.9rem", display:"flex", alignItems:"center", gap:"0.75rem", flexWrap:"wrap" }}>
                    <div style={{ flex:1, minWidth:0 }}>
                      <div style={{ fontSize:"0.68rem", fontWeight:700, color:"rgba(255,255,255,0.3)", textTransform:"uppercase", letterSpacing:"0.05em", marginBottom:3 }}>Student Names</div>
                      <div style={{ fontSize:"0.78rem", fontWeight:600, color:"rgba(255,255,255,0.6)", wordBreak:"break-all", lineHeight:1.5 }}>{allNames || "—"}</div>
                    </div>
                    <button
                      onClick={() => { navigator.clipboard.writeText(allNames); setSessionListNamesCopied(true); setTimeout(() => setSessionListNamesCopied(false), 2000); }}
                      style={{ background: sessionListNamesCopied ? "rgba(34,197,94,0.15)" : "rgba(255,217,61,0.12)", border:`1.5px solid ${sessionListNamesCopied ? "rgba(34,197,94,0.4)" : "rgba(255,217,61,0.3)"}`, color: sessionListNamesCopied ? "#22C55E" : "#FFD93D", borderRadius:8, padding:"5px 12px", cursor:"pointer", fontFamily:"inherit", fontWeight:700, fontSize:"0.75rem", whiteSpace:"nowrap", transition:"all 0.2s" }}>
                      {sessionListNamesCopied ? "✓ Copied!" : "Copy Names"}
                    </button>
                  </div>
                </div>
              )}
              {/* List */}
              <div style={{ overflowY:"auto", flex:1, display:"flex", flexDirection:"column", gap:"0.6rem" }}>
                {signups.length === 0
                  ? <div style={{ textAlign:"center", padding:"2rem", color:"rgba(255,255,255,0.35)", fontWeight:600 }}>No students enrolled yet.</div>
                  : signups.map((r, idx) => (
                    <div key={idx} style={{ background:"rgba(255,255,255,0.04)", border:"1.5px solid rgba(255,255,255,0.09)", borderRadius:12, padding:"0.85rem 1rem", display:"flex", alignItems:"center", gap:"0.85rem", flexWrap:"wrap" }}>
                      <div style={{ width:36, height:36, borderRadius:"50%", background:"rgba(255,217,61,0.15)", border:"1.5px solid rgba(255,217,61,0.3)", display:"flex", alignItems:"center", justifyContent:"center", fontSize:"1rem", flexShrink:0 }}>🧑‍🚀</div>
                      <div style={{ flex:1, minWidth:0 }}>
                        <div style={{ fontWeight:800, fontSize:"0.9rem" }}>{r.studentName}</div>
                        <div style={{ fontSize:"0.76rem", fontWeight:600, color:"rgba(255,255,255,0.42)" }}>{r.parentName} · {r.parentEmail}</div>
                        {r.grade && <div style={{ fontSize:"0.73rem", fontWeight:600, color:"rgba(255,255,255,0.3)", marginTop:1 }}>Grade {r.grade}</div>}
                      </div>
                      <div style={{ display:"flex", flexDirection:"column", alignItems:"flex-end", gap:3, flexShrink:0 }}>
                        <Badge color={r.paid ? "#22C55E" : "#F59E0B"} small>{r.paid ? "✓ Paid" : "⏳ Unpaid"}</Badge>
                        {r.paid && r.amount_paid != null && <Badge color="rgba(34,197,94,0.5)" small>💰 ${r.amount_paid}</Badge>}
                        {r.paid && r.paid_at && <span style={{ fontSize:"0.68rem", fontWeight:700, color:"rgba(255,255,255,0.3)" }}>{new Date(r.paid_at).toLocaleDateString("en-US", { month:"short", day:"numeric", year:"numeric" })}</span>}
                        {r.paid && (() => {
                          if (r.sessionPrice === 0) return <Badge color="#60A5FA" small>🎁 Free</Badge>;
                          const parts = [];
                          if (r.promo_code) parts.push(`Promo: ${r.promo_code}`);
                          if (r.sibling_discount) parts.push("Sibling Discount");
                          if (parts.length > 0) return parts.map((p) => <Badge key={p} color="#A78BFA" small>🎟️ {p}</Badge>);
                          if (r.amount_paid != null && r.amount_paid < r.sessionPrice) return <Badge color="#A78BFA" small>🎟️ Discounted</Badge>;
                          return <Badge color="#60A5FA" small>💳 Credit Card</Badge>;
                        })()}
                      </div>
                    </div>
                  ))
                }
              </div>
              {/* Footer */}
              <div style={{ marginTop:"1.2rem", display:"flex", justifyContent:"flex-end" }}>
                <Btn onClick={() => setViewingSessionSignups(null)} variant="ghost">Close</Btn>
              </div>
            </div>
          </div>
        );
      })()}

      {/* ── PASSWORD RESET MODAL ── */}
      {pwReset && (
        <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.78)", zIndex:300, display:"flex", alignItems:"center", justifyContent:"center", padding:"1rem" }}>
          <div style={{ background:"rgba(10,13,50,0.98)", border:"1.5px solid rgba(245,158,11,0.3)", borderRadius:24, padding:"2.2rem", maxWidth:400, width:"100%", backdropFilter:"blur(20px)" }}>
            <div style={{ fontFamily:"'Fredoka One',cursive", fontSize:"1.5rem", marginBottom:"0.4rem" }}>🔑 Reset Password</div>
            <div style={{ color:"rgba(255,255,255,0.45)", fontSize:"0.84rem", fontWeight:600, marginBottom:"1.4rem" }}>
              Setting new password for <span style={{ color:"#F59E0B", fontWeight:800 }}>{pwReset.name}</span>
            </div>
            {pwSuccess
              ? (
                <div style={{ textAlign:"center", padding:"1.5rem 0" }}>
                  <div style={{ fontSize:"2.5rem", marginBottom:"0.75rem" }}>✅</div>
                  <div style={{ fontWeight:800, color:"#22C55E", marginBottom:6 }}>Password updated!</div>
                  <div style={{ color:"rgba(255,255,255,0.5)", fontSize:"0.85rem", fontWeight:600, marginBottom:"1.4rem" }}>Make sure to share the new credentials securely.</div>
                  <Btn onClick={() => { setPwReset(null); setPwSuccess(false); }} variant="ghost">Done</Btn>
                </div>
              )
              : (
                <div style={{ display:"flex", flexDirection:"column", gap:"0.85rem" }}>
                  <div>
                    <div style={{ fontSize:"0.78rem", fontWeight:700, color:"rgba(255,255,255,0.45)", textTransform:"uppercase", letterSpacing:"0.05em", marginBottom:5 }}>New Password</div>
                    <div style={{ position:"relative" }}>
                      <input
                        type={pwVisible ? "text" : "password"}
                        value={pwValue}
                        onChange={(e) => setPwValue(e.target.value)}
                        placeholder="Create a new password"
                        style={{ width:"100%", background:"rgba(255,255,255,0.06)", border:"1.5px solid rgba(255,255,255,0.14)", borderRadius:10, padding:"10px 42px 10px 14px", color:"#fff", fontFamily:"inherit", fontWeight:600, fontSize:"0.88rem", outline:"none", boxSizing:"border-box" }}
                      />
                      <button
                        onClick={() => setPwVisible((v) => !v)}
                        style={{ position:"absolute", right:10, top:"50%", transform:"translateY(-50%)", background:"none", border:"none", color:"rgba(255,255,255,0.4)", cursor:"pointer", fontSize:"1rem", lineHeight:1, padding:0 }}>
                        {pwVisible ? "🙈" : "👁"}
                      </button>
                    </div>
                    {pwValue.length > 0 && <PasswordChecklist password={pwValue} />}
                  </div>
                  <label style={{ display:"flex", alignItems:"center", gap:"0.6rem", cursor:"pointer", padding:"0.65rem 0.85rem", background:"rgba(245,158,11,0.07)", border:"1.5px solid rgba(245,158,11,0.25)", borderRadius:10 }}>
                    <input
                      type="checkbox"
                      checked={pwConfirmed}
                      onChange={(e) => setPwConfirmed(e.target.checked)}
                      style={{ width:16, height:16, accentColor:"#F59E0B", cursor:"pointer", flexShrink:0 }}
                    />
                    <span style={{ fontSize:"0.81rem", fontWeight:700, color:"rgba(255,255,255,0.75)" }}>
                      I confirm I want to reset the password for <span style={{ color:"#F59E0B" }}>{pwReset.name}</span>
                    </span>
                  </label>
                  {pwError && <p style={{ color:"#FF6B6B", fontSize:"0.82rem", fontWeight:700, margin:0 }}>{pwError}</p>}
                  <div style={{ display:"flex", gap:"0.7rem", marginTop:"0.3rem" }}>
                    <Btn onClick={() => setPwReset(null)} variant="ghost" style={{ flex:"0 0 auto" }}>Cancel</Btn>
                    <Btn onClick={handleResetPassword} variant="primary" style={{ flex:1 }} disabled={pwSaving || !isPasswordStrong(pwValue) || !pwConfirmed}>
                      {pwSaving ? "Updating…" : "Set Password ✓"}
                    </Btn>
                  </div>
                </div>
              )
            }
          </div>
        </div>
      )}
    </div>
  );
}
